/**
 * Step machine + generation/apply logic for the AI Workspace.
 * Extends the useAiDialogFlow pattern with scope calculation and
 * unified generation for enhance, video, and continuous tasks.
 */

import { useState, useCallback, useMemo, useEffect, useRef, type SetStateAction } from 'react';
import i18next from 'i18next';
import { trackEvent } from '@/telemetry/trackEvent.ts';

export type AiWorkspaceScope = 'all' | 'selectedScenes' | 'selectedShots';
import { useProjectStore, retimeShotToVideo } from '@/store/projectStore.ts';
import { usePlaybackStore } from '@/store/playbackStore.ts';
import { useAiStore } from '@/store/aiStore.ts';
import { useUiStore, type AiWorkspaceTask, type AiWorkspaceTab, type AiWorkspacePromptHandoff } from '@/store/uiStore.ts';
import { useCommentStore } from '@/store/commentStore.ts';
import { useTutorialStore, selectExampleTourActive } from '@/store/tutorialStore.ts';
import { applyBatchGenerations, revertShotToLive, reapplyHistoryEntry, revertAnimaticForEnhance } from '@/store/actions.ts';
import { requestAnimaticEnhanceConfirm } from '@/store/animaticEnhanceConfirmStore.ts';
import { showToast } from '@/components/Toast.tsx';
import { buildPromptContext, buildMetadataSuffix, buildVideoPrompt, buildTransitionVideoPrompt, buildTextToImagePrompt, DEFAULT_VIDEO_PROMPT, STYLE_PRESET_PHOTO, STYLE_PRESET_SKETCH, STYLE_PRESET_ANIME } from '@/ai/promptBuilder.ts';
import { DEFAULT_SEED_ASPECT } from '@/ai/blankSeed.ts';
import { parseAspectRatio } from '@/utils/aspectRatio.ts';
import { isReadOnly } from '@/store/guards.ts';
import { capabilities } from '@/capabilities.ts';
import { composeShotNumberLabel } from '@/components/SceneBoard/sceneCaption.ts';
import { predictInsertedShotNumber } from '@/utils/predictInsertedShotNumber.ts';
import { readAssetBytes, createAssetUrl } from '@/previs/assetStorage.ts';

export type StylePreset = 'photo' | 'sketch' | 'anime';

const STYLE_PRESET_PROMPTS: Record<StylePreset, string> = {
  photo: STYLE_PRESET_PHOTO,
  sketch: STYLE_PRESET_SKETCH,
  anime: STYLE_PRESET_ANIME,
};

/** "Is the textarea still holding an untouched preset?" — the test for whether it is safe to clear. */
const PRESET_PROMPT_SET = new Set<string>(Object.values(STYLE_PRESET_PROMPTS));

/**
 * Drop enhance-mode boilerplate on the way into text-only mode, keeping anything the user added.
 *
 * Exact equality against the preset constants is not a sufficient test on its own: one typed
 * character makes an EDITED preset read as "user text", and it still carries "@image1", "Maintain
 * 1:1 spatial blocking" and — worst — "Replace stock avatars with anatomically correct human
 * figures in the same exact coordinates", which against a flat-gray seed plate means putting
 * PEOPLE into an empty establishing shot.
 *
 * Only a LEADING preset is removed. The words the user appended are theirs and survive verbatim,
 * and text that merely contains a preset somewhere in the middle is left alone rather than cut up
 * — the invariant is still "never clear text the user typed".
 */
function stripPresetBoilerplate(prev: string): string {
  const trimmed = prev.trim();
  if (PRESET_PROMPT_SET.has(trimmed)) return '';
  for (const preset of PRESET_PROMPT_SET) {
    if (trimmed.startsWith(preset)) return trimmed.slice(preset.length).trim();
  }
  return prev;
}

function loadStylePreset(): StylePreset {
  const stored = localStorage.getItem('previs-style-preset');
  if (stored === 'photo' || stored === 'sketch' || stored === 'anime') return stored;
  return 'sketch';
}

/**
 * The style preset the surface OPENS on.
 *
 * The tour's render beat always lands the same pre-baked SKETCH, but the preset is a persisted
 * GLOBAL preference shared with the Timeline AI Workspace — so anyone who has ever tapped Photo
 * carries Photo into the tour and watches a Photo-selected control produce a pencil sketch.
 *
 * The selection is SEEDED, never forced: nothing is written to localStorage, so the user's real
 * preference is untouched and back in force the moment the tour ends, and handleStylePresetChange
 * stays fully live (deriving an override that pinned the display while leaving the buttons
 * pressable would make Photo look broken). Scoped to the board dialog, where the beat now runs —
 * the Timeline workspace shares this hook and must be bit-for-bit unaffected.
 */
function initialStylePreset(hostOpts?: AiWorkspaceFlowHost): StylePreset {
  // 20260921 — EXAMPLE-ONLY (design §2.1): the seeded preset exists because the Getting started
  // tour's render beat always lands a baked SKETCH. A characters run generates for real and keeps
  // the user's own persisted preference.
  const tutorial = useTutorialStore.getState();
  if (hostOpts?.host === 'boardDialog' && selectExampleTourActive(tutorial) && tutorial.step === 'render') {
    return 'sketch';
  }
  return loadStylePreset();
}
import {
  estimateCost, getModelCost, getModelEstSeconds, getDurationLimits, clampDuration,
  FALLBACK_CATALOG,
} from '@/ai/tokenCosts.ts';
import { describeAiError } from '@/ai/aiErrorMessages.ts';
import { diagLog } from '@/bridge/embeddedBridge.ts';
import type { TimelineShot, TimelineScene } from '@/types/timeline.ts';
import type { CatalogModel } from '@/types/ai.ts';

// ─── Types ───────────────────────────────────────────────────────────────

export type WorkspaceStep = 'options' | 'generating' | 'review' | 'done';

export type GenerationUnit =
  | { type: 'shot'; shot: TimelineShot }
  | { type: 'continuous'; fromShot: TimelineShot; toShot: TimelineShot };

export interface ImageReviewItem {
  id: string;
  kind: 'image';
  shotId: number;
  success: boolean;
  enhancedDataUrl?: string;
  originalDataUrl?: string;
  prompt?: string;
  seed?: number | null;
  tokenCost?: number;
  historyId?: string;
  error?: string;
  accepted: boolean;
}

export interface VideoReviewItem {
  id: string;
  kind: 'video';
  unitType: 'shot' | 'continuous';
  shotId: number;
  fromShotId?: number;
  toShotId?: number;
  success: boolean;
  videoUrl?: string;
  sourceThumb?: string;
  sourceThumb2?: string;
  prompt?: string;
  seed?: number | null;
  tokenCost?: number;
  historyId?: string;
  /**
   * Seconds the model was ASKED for (clampDuration'd to what it supports), i.e. the length of the
   * file it returned. Recorded 20260909 (owner) for the one caller that cannot measure the blob:
   * "Add as new shot" retimes AFTER the insert's repush has revoked the URL. See applyMediaToShot.
   */
  duration?: number;
  error?: string;
  accepted: boolean;
}

export type ReviewItem = ImageReviewItem | VideoReviewItem;

export interface GenerationProgress {
  current: number;
  total: number;
  message: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function unitKey(unit: GenerationUnit): string {
  return unit.type === 'continuous' ? `cont-${unit.fromShot.id}` : `shot-${unit.shot.id}`;
}

function shotFullLabel(shot: TimelineShot, scenes: readonly TimelineScene[], separator: string): string {
  const scene = scenes.find(s => s.id === shot.sceneId);
  const sceneNum = scene?.number ?? '?';
  return `${sceneNum}${separator}${shot.number || '?'}`;
}

function unitLabel(unit: GenerationUnit, scenes: readonly TimelineScene[], separator: string): string {
  if (unit.type === 'continuous') {
    const fromLabel = shotFullLabel(unit.fromShot, scenes, separator);
    const toLabel = shotFullLabel(unit.toShot, scenes, separator);
    return `${fromLabel}\u2013${toLabel}`;
  }
  return shotFullLabel(unit.shot, scenes, separator);
}

// ─── "Add as new shot" (Create tab) ──────────────────────────────────────

/** Same budget the board's own insert/paste/add flows use (SceneBoardExpanded.tsx). */
const SHOT_MUTATE_TIMEOUT_MS = 30000;

/** Whichever of the two media kinds the Create run produced, already CAPTURED into plain values. */
export interface CreatedShotMedia {
  enhancedDataUrl?: string;
  videoUrl?: string;
  historyId?: string;
  /**
   * Poster frame (base64 JPEG, no data: prefix) extracted BEFORE the insert, while the video blob
   * was still alive — 20260909 (owner). Null means the extraction failed and attachVideo falls back
   * to its own (which, post-repush, will fail too — the shot then keeps its live render, as today).
   */
  posterBase64?: string | null;
  /** Video length in seconds, captured pre-insert for the same reason. Null = retime the old way. */
  durationSeconds?: number | null;
  /**
   * The video's BYTES, read BEFORE the insert while `videoUrl` is still alive (20260925, owner item
   * 15c). The insert's repush revokes `videoUrl` (loadPrevisFile's clearAssetByteCache and
   * revokeOldBlobUrls), so attaching it after the insert left the new shot with a play badge over a
   * dead blob — nothing played until a canvas visit extracted {shotId}.mp4. applyMediaToShot mints
   * a fresh URL from these. Null = the read failed; the attach then falls back to `videoUrl`.
   */
  videoBytes?: Uint8Array | null;
}

/**
 * Ask Unity for a new shot after `anchorShotId` and resolve with its id.
 *
 * Module-level, and taking every input as a plain argument, on purpose. It outlives the component:
 * the same chain runs from the review footer's Apply AND from the unmount teardown, which fires
 * while the workspace is already coming down. Nothing here may read hook state.
 *
 * Ordering is load-bearing and is the board's `runAddShotWithImage` contract verbatim:
 *   clearMutateResult → insertShot → the `shotMutated` ack (which CARRIES the new id) → the
 *   presence gate, because the row only exists once Unity's setSqlitePath repush reconciles.
 * Applying before that gate writes to a shot id that does not exist yet, and a board-route repush
 * loads the slim DB whose `shots.staticimagedata` is blanked, so an early image is simply erased.
 *
 * Returns null when the create failed — reported through `onError`, and the caller keeps the
 * generation and stays in review rather than refunding, because nothing was lost but the insert.
 * The dynamic imports are deliberately OUTSIDE the try: a module that will not load is not a shot
 * mutate timeout, and mislabelling it would send the user looking for Unity. It throws instead, to
 * the caller's own catch.
 */
async function insertShotForCreate(
  anchorShotId: number,
  onError: (message: string) => void,
): Promise<number | null> {
  const [{ sendInsertShot }, waits, { useShotSettingsStore }] = await Promise.all([
    import('@/bridge/features/shotSettings.ts'),
    import('@/integration/waitForDeleteReconcile.ts'),
    import('@/store/shotSettingsStore.ts'),
  ]);

  try {
    // waitForShotMutateResult matches on `op` and the store keeps only the LAST ack, so the
    // previous one has to be cleared before the send or a stale insert ack resolves instantly.
    useShotSettingsStore.getState().clearMutateResult();
    sendInsertShot(anchorShotId);
    const ack = await waits.waitForShotMutateResult('insert', SHOT_MUTATE_TIMEOUT_MS);
    if (ack.error) {
      onError(i18next.t(`common:${ack.error.messageKey}`));
      return null;
    }
    await waits.waitForShotPresent(ack.shotId, SHOT_MUTATE_TIMEOUT_MS);
    return ack.shotId;
  } catch {
    onError(i18next.t('common:shotMutateTimeout'));
    return null;
  }
}

/**
 * Put already-captured media on a shot that exists.
 *
 * Split out of the chain below so that a RETRY after a half-finished Apply can go straight here
 * against the shot the first attempt created, instead of inserting a second one.
 *
 * THROWS on failure (applyBatchGenerations owns an undo snapshot and its own bridge sends;
 * attachVideo writes the store and retimeShotToVideo drives a <video> element). Every caller needs
 * a catch — this runs inside the applyBusy latch, and an escaping rejection freezes the review.
 */
async function applyMediaToShot(shotId: number, media: CreatedShotMedia): Promise<void> {
  if (media.enhancedDataUrl) {
    // The only sanctioned image write — it owns the undo snapshot, the sceneReadyBuffer flush and
    // the historyId FK sync. Never hand-roll the bridge sends here.
    applyBatchGenerations([{
      shotId,
      enhancedDataUrl: media.enhancedDataUrl,
      historyId: media.historyId!,
    }]);
  } else if (media.videoUrl) {
    // 20260925 (owner item 15c): media.videoUrl is DEAD here — the insert's repush revoked it — so
    // re-mint a live URL from the bytes captured before the insert and repoint the history entry at
    // it, the reapplyHistoryEntry pattern (store/actions.ts). createAssetUrl also registers the bytes
    // in the asset byte cache, which is what attachVideo's audio extraction reads.
    const liveUrl = media.videoBytes ? createAssetUrl(media.videoBytes, 'video/mp4') : media.videoUrl;
    if (media.videoBytes && media.historyId) {
      useAiStore.getState().updateHistoryEntryUrl(media.historyId, liveUrl);
    }
    useProjectStore.getState().attachVideo(
      shotId, liveUrl, 'video/mp4', media.historyId,
      { posterBase64: media.posterBase64 },
    );
    if (useAiStore.getState().retimeToMatchVideo) {
      // 20260909 (owner): by the time this runs the insert's repush has revoked media.videoUrl, so
      // retimeShotToVideo's <video> errors out and the retime silently never happens. Use the length
      // captured before the insert instead; the URL path stays as the fallback for a media object
      // that carries no duration (nothing constructs one today — defensive only).
      const captured = media.durationSeconds;
      if (captured != null && isFinite(captured) && captured > 0) {
        useProjectStore.getState().updateShotDuration(shotId, captured, { pushUndo: false });
      } else {
        await retimeShotToVideo(shotId, liveUrl);
      }
    }
  }
}

/**
 * Create a shot in Unity, then put the generated media on it — the "Add as new shot" chain.
 *
 * The caller must CAPTURE `media` before calling: the insert's repush is a full project reload that
 * resets aiStore.generationHistory, projectStore.videoShots and the undo stack, so the review
 * item's own fields can be gone by the time this resolves.
 *
 * Returns the new shot id, or null when the CREATE failed. A failure of the media half instead
 * REJECTS, and the id is then unrecoverable from here — callers that need to retry must use the two
 * halves directly (handleApply does, through createdShotIdRef).
 */
async function insertShotAndApplyMedia(
  anchorShotId: number,
  media: CreatedShotMedia,
  onError: (message: string) => void,
): Promise<number | null> {
  const newShotId = await insertShotForCreate(anchorShotId, onError);
  if (newShotId == null) return null;
  await applyMediaToShot(newShotId, media);
  return newShotId;
}

// ─── Host ────────────────────────────────────────────────────────────────

/**
 * Which surface is driving this flow. Every field is optional and every default reproduces the
 * shipped Timeline AI Workspace exactly — calling the hook with no argument must stay
 * observably identical to before this parameter existed (see useAiWorkspaceFlow.host.test.ts).
 */
export interface AiWorkspaceFlowHost {
  /** Which surface owns this flow. Default 'workspace' = the Timeline AI Workspace. */
  host?: 'workspace' | 'boardDialog';
  /** Overrides uiStore.aiWorkspaceOpen as the flow's armed flag. */
  open?: boolean;
  /** Seeds the selection on open, in place of uiStore.aiWorkspacePreSelectedShotId. */
  preSelectedShotId?: number | null;
  /** Called where the flow closes its own host (default: uiStore.closeAiWorkspace). */
  onCloseHost?: () => void;
  /**
   * Pins the IMAGE model for this flow in place of aiStore.aiSelectedImageModel. Video is
   * untouched — its picker stays live wherever the host renders one.
   *
   * A host that drops its image-model picker cannot get there by writing the store:
   * aiSelectedImageModel is a GLOBAL, localStorage-persisted preference shared with the Timeline
   * AI Studio, ViewerOverlay and the ShotBar's quick generate, so a mount-time write silently
   * rewrites the user's saved choice everywhere else. Nor is hiding the control enough — the
   * request, the cost badge, the affordability gate and both telemetry payloads all resolve the
   * model from HERE, so a hidden picker over an unchanged store spends whatever was last picked
   * in AI Studio while the UI claims otherwise. One override, read by all of them.
   */
  forcedImageModelId?: string;
  /**
   * Pins the VIDEO model, exactly as forcedImageModelId pins the image one — read the note above,
   * it applies verbatim. Two extra traps on this side: handleVideoGenerate used to resolve the id
   * with `useAiStore.getState().aiSelectedVideoModel` at call time, in TWO places (the
   * generate_tapped payload and the id the request itself carries), so a pin that only reached the
   * cost memos would quote one model and bill another. Both now go through the same binding.
   */
  forcedVideoModelId?: string;
  /**
   * This host's VIDEO arm is a shot description over a gray plate, not a motion prompt over the
   * shot's frame (the board's Sketch-with-AI dialog, owner 2026-08-25).
   *
   * Four coupled behaviours, which is why it is one flag rather than four:
   *   • the prompt box is the USER's — no DEFAULT_VIDEO_PROMPT seeding, and a task switch does not
   *     re-seed or clear it;
   *   • the request carries `textOnly: true` + `seedAspect`, the same pair the Create tab passes;
   *   • a thumbnail-less shot still reaches the generator (the plate is the input, not the frame);
   *   • and none of it touches the shared `textOnly` state, which belongs to the "Include image"
   *     checkbox and is force-cleared for every non-enhance task anyway.
   *
   * Default false: the Timeline AI Workspace's video arm animates the real frame and must stay
   * bit-for-bit unaffected (orchestratorFullResWiring.test.ts pins the non-textOnly path).
   */
  videoTextOnly?: boolean;
  /**
   * DEFERRED CREATE (2026-08-28, owner item 3) — the board's Add-shot chooser, Sketch row.
   *
   * ARMED (non-undefined) means THIS RUN HAS NO TARGET SHOT AT ALL. The shot the flow was seeded
   * with is an ANCHOR, borrowed for `scopeShots`, the seed aspect and the cost estimate, and it
   * must never be written to. Apply calls this instead of insertShotForCreate, and whatever id it
   * resolves is what the media lands on.
   *
   * Supplied as a callback rather than a `sceneId` because the create chain belongs to the board:
   * `addShotToScene` is a raw verb with no named sender, and its eight-mock rationale lives at
   * SceneBoardExpanded.runAddShot. Inventing a second Unity verb here — or reusing insertShot
   * against the anchor — would also silently diverge from the Add-shot button, which APPENDS and
   * assigns no number variant where insert duplicates in place and does (TimelineWebViewPanel's
   * AddShotToSceneFromBoard vs HandleInsertShotFromWeb).
   *
   * Resolves null when the create failed, exactly like insertShotForCreate: the caller stays in
   * review holding the paid generation, with no refund, and Apply is pressable again.
   *
   * A NON-NULL id means "Unity has made this shot", NOT "the web store can see it yet". The board's
   * implementation resolves the acked id even when its own repush-reconcile gate times out, because
   * the alternative — reporting null for a shot that exists — is what lets a later path create a
   * second one (see SceneBoardExpanded.createShotForDeferredSketch).
   *
   * Three auto-apply paths change meaning when this is armed — see handleCancel, handleEnhance's
   * abort catch (both DROP the completed image and refund) and resolvePendingReviewOnTeardown
   * (which creates and applies).
   */
  onCreateShotForApply?: () => Promise<number | null>;
}

// ─── Hook ────────────────────────────────────────────────────────────────

export function useAiWorkspaceFlow(hostOpts?: AiWorkspaceFlowHost) {
  const storeOpen = useUiStore(s => s.aiWorkspaceOpen);
  const open = hostOpts?.open ?? storeOpen;
  const task = useUiStore(s => s.aiWorkspaceTask);
  const initialPair = useUiStore(s => s.aiWorkspaceInitialPair);
  const closeAiWorkspace = useUiStore(s => s.closeAiWorkspace);
  const closeWorkspace = hostOpts?.onCloseHost ?? closeAiWorkspace;
  // The open/close effect below is keyed on `open` alone (shipped shape) and must not re-run
  // when a host hands in a fresh options object every render, so it reads the host through a
  // ref rather than through the dependency list.
  const hostRef = useRef(hostOpts);
  hostRef.current = hostOpts;

  const project = useProjectStore(s => s.project);

  const aiUseMetadata = useAiStore(s => s.aiUseMetadata);
  const toggleAiUseMetadata = useAiStore(s => s.toggleAiUseMetadata);
  const aiSelectedImageModel = useAiStore(s => s.aiSelectedImageModel);
  const aiSelectedVideoModel = useAiStore(s => s.aiSelectedVideoModel);
  const setSelectedModel = useAiStore(s => s.setSelectedModel);
  const aiModelCatalog = useAiStore(s => s.aiModelCatalog);
  const retimeToMatchVideo = useAiStore(s => s.retimeToMatchVideo);
  const toggleRetimeToMatchVideo = useAiStore(s => s.toggleRetimeToMatchVideo);
  const aiAudioHints = useAiStore(s => s.aiAudioHints);
  const setAiAudioHint = useAiStore(s => s.setAiAudioHint);

  const baseCatalog: CatalogModel[] = aiModelCatalog.length > 0 ? aiModelCatalog : FALLBACK_CATALOG;
  // Ensure touchup models are always present (server may not include them yet)
  const catalog: CatalogModel[] = baseCatalog.some(m => m.category === 'touchup')
    ? baseCatalog
    : [...baseCatalog, ...FALLBACK_CATALOG.filter(m => m.category === 'touchup')];
  const isEnhance = task === 'enhance';
  // EVERY image-model read below goes through this one binding — request, cost, estimate,
  // telemetry — so a host that pins the model (forcedImageModelId) cannot leave any of them
  // disagreeing. aiStore is never written; the user's AI Studio preference survives.
  const imageModelId = hostOpts?.forcedImageModelId ?? aiSelectedImageModel;
  // Same rule on the video side, and the same reason: cost badge, affordability gate, estimate,
  // both telemetry payloads and the request all resolve the id from HERE and nowhere else.
  const videoModelId = hostOpts?.forcedVideoModelId ?? aiSelectedVideoModel;
  const activeModelId = isEnhance ? imageModelId : videoModelId;
  /** See AiWorkspaceFlowHost.videoTextOnly — the board dialog's plate-based video arm. */
  const videoTextOnly = hostOpts?.videoTextOnly === true;

  // ── Step machine ──────────────────────────────────────────────────────
  const [step, setStep] = useState<WorkspaceStep>('options');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [progress, setProgress] = useState<GenerationProgress | null>(null);
  const [completedMedia, setCompletedMedia] = useState<Map<string, string>>(new Map());
  const completedMediaRef = useRef<Map<string, string>>(new Map());
  const [reviewItems, setReviewItems] = useState<ReviewItem[]>([]);
  const abortRef = useRef<AbortController | null>(null);
  const itemsRef = useRef<ReviewItem[]>([]);
  const priorityQueueRef = useRef<TimelineShot[]>([]);
  /** Per-shot prompt overrides for touch-up from preview. Shot ID → { prompt, imageDataUrl }. */
  const touchUpOverridesRef = useRef<Map<number, { prompt: string; imageDataUrl: string }>>(new Map());
  const [previewShotId, setPreviewShotId] = useState<number | null>(null);

  /**
   * One-shot prompt hand-off from BoardEnhanceDialog's "Open AI Studio" (2026-09-01, owner item C).
   *
   * Consumed ONCE, at mount, and by the WORKSPACE host only — the board dialog is the ARMER and
   * mounts this same hook, so it must never read its own latch back (the dialog's instance is alive
   * across the arming click, and a consuming read there would swallow the hand-off before the
   * workspace ever mounted). `hostOpts?.host` rather than hostRef, exactly as initialStylePreset
   * reads it: this runs during the first render, before any ref assignment would help.
   *
   * A LAZY INITIALIZER for the same reasons createAddAsNewShot uses one: the latch is read-and-clear
   * so it must fire exactly once, and a reactive read would re-seed the box after the user had
   * typed over it.
   */
  const [promptHandoff] = useState<AiWorkspacePromptHandoff | null>(
    () => (hostOpts?.host === 'boardDialog' ? null : useUiStore.getState().consumeAiWorkspacePromptHandoff()),
  );
  /**
   * The Enhance half of that hand-off, parked for the seeding effect below.
   *
   * The Enhance box is not plain state the way createPrompt is — the seeding effect writes it from
   * `generatedPrompt` on mount AND on every later change, so an initial `useState` value would be
   * overwritten a tick later. The ref lets the hand-off win the seeds and then get out of the way.
   */
  const enhanceHandoffRef = useRef<string | null>(
    promptHandoff?.target === 'enhance' ? promptHandoff.prompt : null,
  );
  /**
   * The `generatedPrompt` the hand-off above was seeded against — the flag for "has a REAL re-seed
   * happened yet?", and the reason the hand-off is not simply consumed by its first pass.
   *
   * The seeding effect does not only fire on a generatedPrompt change: `computedMetadata` is in its
   * dependency list too, and that settles a tick after the mount, when the open-effect swaps the
   * initial 'all' scope for the one pre-selected shot. On any shot from a different scene than the
   * project's first that second pass re-runs with generatedPrompt UNCHANGED — so releasing the
   * hand-off on the first pass would hand the box straight back to the preset boilerplate the user
   * came here to replace, and the feature would work for shots in the project's FIRST SCENE only
   * (pinned in useAiWorkspaceFlow.promptHandoff.test.ts).
   *
   * The hand-off is therefore released on the first seeding pass whose `generatedPrompt` differs
   * from the one recorded here, OR on any USER-OWNED write of the box —
   * `setEditablePromptOwned` below, `handlePromptReset` and `handleStylePresetChange`. Both halves
   * are needed: the second covers the writes that rewrite the box without moving generatedPrompt at
   * all (Reset prompt, and re-tapping the already-active preset, which neither tab guards against).
   */
  const enhanceHandoffSeedRef = useRef<string | null>(null);

  // Prompt state
  const [editablePrompt, setEditablePrompt] = useState('');
  /**
   * The setter the UI gets — the raw one stays internal.
   *
   * A user write ENDS the hand-off's tenure: it was a one-time seed, and re-asserting it over a box
   * the user has since taken ownership of is the same clobber enhanceHandoffRef exists to prevent.
   * The hook's own writers (the seeding effect, the task force-back, setTextOnly) deliberately keep
   * the raw setter — none of them is the user reaching for the keyboard.
   */
  const setEditablePromptOwned = useCallback((value: SetStateAction<string>) => {
    enhanceHandoffRef.current = null;
    setEditablePrompt(value);
  }, []);
  const [touchUpPrompt, setTouchUpPrompt] = useState('');
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptInitialized, setPi] = useState(false);
  const [stylePreset, setStylePresetState] = useState<StylePreset>(() => initialStylePreset(hostOpts));

  /**
   * "Include image" unchecked — generate the shot from the prompt alone (spec addendum 2026-08-21).
   *
   * DEFAULT FALSE and only ever set by the board's Enhance dialog: the Timeline AI Workspace shares
   * this hook and must be bit-for-bit unaffected. Image task only — there is no text-to-video
   * (both video actions hard-require an input frame), so the effect below forces it back off.
   */
  const [textOnly, setTextOnlyState] = useState(false);
  // Read by the prompt-seeding effect, which must NOT re-run merely because this flipped: toggling
  // the checkbox is itself a prompt decision (setTextOnly makes it) and a re-seed on the way back
  // would overwrite whatever the user had typed. Same reason hostRef exists above.
  const textOnlyRef = useRef(textOnly);
  textOnlyRef.current = textOnly;
  /**
   * The new task's own prompt, kept fresh during render (assigned right below the generatedPrompt
   * memo) so the force-back effect can read it without joining a dependency list.
   */
  const generatedPromptRef = useRef('');
  /**
   * "The video box holds the user's own words on this host, so nothing may re-seed it."
   *
   * Assigned during render — the shipped idiom for this file (hostRef, generatedPromptRef) — so
   * the two prompt effects can read it without joining a dependency list and changing WHEN they
   * run. True only for a videoTextOnly host on the video task; every other caller is untouched.
   */
  const videoPromptIsUserOwnedRef = useRef(false);
  videoPromptIsUserOwnedRef.current = videoTextOnly && task === 'video';
  useEffect(() => {
    if (task === 'enhance') return;
    // A videoTextOnly host has the OPPOSITE problem: both arms are the same shot-description box
    // and the video request is a gray plate, so the words in the box are the ones to keep and
    // re-seeding would throw them away. What must NOT survive is the image arm's preset
    // boilerplate — "@image1", "Maintain 1:1 spatial blocking" and above all "Replace stock avatars
    // with anatomically correct human figures in the same exact coordinates", which against a flat
    // plate means putting people in an empty establishing shot. Same custody the "Include image"
    // checkbox applies on the way out (setTextOnly), for the same reason.
    if (videoPromptIsUserOwnedRef.current) {
      if (textOnlyRef.current) setTextOnlyState(false);
      setEditablePrompt(prev => stripPresetBoilerplate(prev));
      return;
    }
    if (!textOnlyRef.current) return;
    setTextOnlyState(false);
    // Resetting the FLAG is not enough. The textarea is shared between the two tasks, and whatever
    // it holds at the moment of the switch is what handleVideoGenerate sends as customVideoPrompt
    // — a stranded shot description becomes the MOTION prompt of a paid video generation. The
    // seeding effect below cannot cover it: it runs EARLIER in this same flush and reads
    // textOnlyRef, still true here, and by the next render every one of its deps is value-equal
    // again (the video prompt is the identical string before and after), so it never re-runs.
    // Re-seed here instead — generatedPrompt has already recomputed to the video prompt for this
    // render — which reproduces exactly what the checked-box path does on the same switch.
    setEditablePrompt(generatedPromptRef.current);
  }, [task]);

  // Video options
  const [generateAudio, setGenerateAudio] = useState(false);

  // ── Scope ─────────────────────────────────────────────────────────────
  const [scope, setScope] = useState<AiWorkspaceScope>('all');
  const [selectedSceneId, setSelectedSceneId] = useState<number | null>(null);
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [skipApproved, setSkipApproved] = useState(true);

  const allScenes = project?.scenes ?? [];
  // Declared here rather than beside progressItems (where it used to live): the Create tab's
  // predicted-label memo below needs it, and a `const` referenced above its own declaration is a
  // TDZ throw inside a useMemo factory, not a hoist.
  const separator = project?.documentSettings?.separator ?? '';

  // ── V2 selection state ────────────────────────────────────────────────
  const [selectedShotIds, setSelectedShotIds] = useState<Set<number>>(new Set());

  // V2: "currentShot" is the first grid-selected shot, not the timeline playhead
  const allShotsFlat = useMemo(() => allScenes.flatMap(s => s.shots), [allScenes]);
  const currentShot = useMemo(() => {
    for (const s of allShotsFlat) {
      if (selectedShotIds.has(s.id)) return s;
    }
    return null;
  }, [allShotsFlat, selectedShotIds]);
  const effectiveSceneId = useMemo(() => {
    if (selectedSceneId != null) return selectedSceneId;
    if (currentShot) {
      return allScenes.find(s => s.shots.some(sh => sh.id === currentShot.id))?.id ?? allScenes[0]?.id;
    }
    return allScenes[0]?.id;
  }, [selectedSceneId, currentShot, allScenes]);

  const toggleShotSelection = useCallback((shotId: number) => {
    setSelectedShotIds(prev => {
      const next = new Set(prev);
      if (next.has(shotId)) next.delete(shotId);
      else next.add(shotId);
      return next;
    });
  }, []);

  const toggleSceneSelection = useCallback((shotId: number) => {
    const scene = allScenes.find(s => s.shots.some(sh => sh.id === shotId));
    if (!scene) return;
    const sceneShots = scene.shots.map(s => s.id);
    setSelectedShotIds(prev => {
      const next = new Set(prev);
      const allSelected = sceneShots.every(id => next.has(id));
      if (allSelected) {
        sceneShots.forEach(id => next.delete(id));
      } else {
        sceneShots.forEach(id => next.add(id));
      }
      return next;
    });
  }, [allScenes]);

  const clearSelection = useCallback(() => {
    setSelectedShotIds(new Set());
  }, []);

  // When true, emptying the selection in selectedShots scope auto-switches to "all".
  // Set to false when the user manually picks "selectedShots" from the dropdown
  // so they can stay in that scope with 0 selections.
  const [autoScopeSwitch, setAutoScopeSwitch] = useState(true);

  // ── V2 touch up target ──────────────────────────────────────────────
  const [touchUpTargetShotId, setTouchUpTargetShotId] = useState<number | null>(null);

  const effectiveTouchUpShot = useMemo(() => {
    if (touchUpTargetShotId) {
      const shot = allShotsFlat.find(s => s.id === touchUpTargetShotId);
      if (shot) return shot;
    }
    return currentShot;
  }, [touchUpTargetShotId, allShotsFlat, currentShot]);

  // ── V2 Create tab (2026-08-25) ────────────────────────────────────────

  // Seeded from the board dialog's prompt hand-off when it named this box (see promptHandoff
  // above); '' — the shipped default — for every other open. Plain state with no re-seeding
  // effect behind it, so the initial value is the whole story here.
  const [createPrompt, setCreatePrompt] = useState(
    () => (promptHandoff?.target === 'create' ? promptHandoff.prompt : ''),
  );
  /**
   * Create's own Image/Video picker.
   *
   * Deliberately NOT uiStore.aiWorkspaceTask: that field is the Enhance tab's task pill AND the
   * board dialog's, and switching Create to Video would silently move Enhance's selection with it
   * (and trip the textOnly force-reset effect above). handleCreateGenerate mirrors this INTO
   * aiWorkspaceTask at the moment it starts a run, because WorkspaceMain's generating/review/apply
   * chrome branches on `isEnhance`, never on the active tab. Presentation state — nothing restores
   * it afterwards, and nothing needs to.
   */
  const [createTask, setCreateTask] = useState<'enhance' | 'video'>('enhance');
  /**
   * "This task change is Create's, not the user's — leave the Enhance textarea alone."
   *
   * A one-shot, consumed by the pair of effects that own the prompt (the seeding effect and the
   * task effect below). handleCreateGenerate mirrors createTask into the SHARED aiWorkspaceTask so
   * the review chrome follows the run, and a real task change re-seeds editablePrompt from the
   * task's generated prompt — which, for a Create video run, silently overwrites whatever the user
   * had typed on the Enhance tab and never gives it back. Cross-tab, cross-task damage from a
   * presentation-only mirror: the flag exists to keep exactly that one transition quiet.
   */
  const suppressTaskPromptReseedRef = useRef(false);
  /**
   * "Add as new shot", seeded once per workspace MOUNT (2026-08-28, owner item 3).
   *
   * false is still the shipped default and every ordinary open gets it. The one caller that seeds
   * true is BoardEnhanceDialog's "Open AI Studio" button in DEFERRED mode: that dialog's whole
   * contract is "generate a NEW shot", and this checkbox is the workspace's own name for exactly
   * that, so the hand-off would otherwise drop the user on a Create tab pointed at the anchor.
   *
   * A LAZY INITIALIZER, and getState() rather than a subscription: the latch is read-and-clear
   * (uiStore.consumeCreateAddAsNewShot), so it must fire exactly once, on mount, and never again on
   * a re-render. Reading it reactively would also make an unrelated store write re-seed the box
   * after the user had unticked it.
   */
  const [createAddAsNewShot, setCreateAddAsNewShot] = useState(
    () => useUiStore.getState().consumeCreateAddAsNewShot(),
  );
  const [createTargetShotId, setCreateTargetShotId] = useState<number | null>(null);

  /**
   * Which handler started the run currently in flight — the signal WorkspaceMain's shared
   * generating/review chrome uses to tell a Create run apart from an Enhance or Touch Up one, and
   * the gate on the "Add as new shot" apply chain. Cleared on every return to the options step.
   *
   * 'import' is the odd one out and the reason several gates below name it explicitly: it is the
   * only origin whose review was never GENERATED — no network call, no credits, no history row —
   * so the close-path auto-apply, the tutorial finale and the scope relaxation all have to skip it.
   */
  const [generationOrigin, setGenerationOrigin] = useState<'enhance' | 'touchup' | 'create' | 'import' | null>(null);
  /** True while the Apply chain's Unity round trip is in flight — the review footer freezes. */
  const [applyBusy, setApplyBusy] = useState(false);

  const effectiveCreateShot = useMemo(() => {
    if (createTargetShotId) {
      const shot = allShotsFlat.find(s => s.id === createTargetShotId);
      if (shot) return shot;
    }
    return currentShot;
  }, [createTargetShotId, allShotsFlat, currentShot]);

  /**
   * Carry the shot across a tab switch (2026-08-25).
   *
   * The four tabs disagree about where "the selected shot" lives: Enhance and Import read
   * `selectedShotIds` directly, while Create and Touch Up each layer a per-tab override on top of
   * it (`createTargetShotId` / `touchUpTargetShotId`, both falling back to `currentShot`). The tab
   * bar used to resolve that by wiping all three, which is why picking a shot on Touch Up and
   * stepping over to Import handed you an empty panel.
   *
   * This collapses the OUTGOING tab's effective shot back into the shared set instead, then drops
   * both overrides so the incoming tab reads the unified value rather than a stale target. Multi-
   * select collapses to the FIRST shot in project order — the same shot `currentShot` already
   * names, and for a continuous pair that is the 'from' shot, matching handleTaskChange's own
   * keep-the-from rule. An empty selection stays empty.
   *
   * Deliberately NOT applied to a tap inside a tab: the per-tab toggle contract (tap the active
   * target → override null → fall back to currentShot) is unchanged and still pinned by
   * CreateTab.test / TouchUpTab.test. Unification happens at the boundary and nowhere else.
   *
   * `scope` is left alone: an All-Shots user keeps All Shots, and since the set comes out non-empty
   * whenever there was a shot, StyleGradeTab's empty-selection auto-flip has nothing to fire on.
   */
  const unifySelectionForTabSwitch = useCallback((fromTab: AiWorkspaceTab) => {
    const outgoing = fromTab === 'create'
      ? effectiveCreateShot
      : fromTab === 'touchup'
        ? effectiveTouchUpShot
        : currentShot;
    setSelectedShotIds(outgoing ? new Set([outgoing.id]) : new Set<number>());
    setTouchUpTargetShotId(null);
    setCreateTargetShotId(null);
  }, [effectiveCreateShot, effectiveTouchUpShot, currentShot]);

  /**
   * The scene-qualified shot label the Create progress line announces.
   *
   * With "Add as new shot" checked the shot does not exist yet, so this is Unity's numbering rules
   * run forward one step (predictInsertedShotNumber) — a prediction, flagged as such there.
   * Unchecked it is simply the selected shot's own label.
   */
  const createProgressLabel = useMemo(() => {
    const shot = effectiveCreateShot;
    if (!shot) return '';
    const scene = allScenes.find(s => s.shots.some(sh => sh.id === shot.id));
    const number = createAddAsNewShot
      ? predictInsertedShotNumber(scene, shot, project?.documentSettings)
      : (shot.number || '?');
    return composeShotNumberLabel(scene?.number ?? '?', separator, number);
  }, [effectiveCreateShot, allScenes, createAddAsNewShot, project?.documentSettings, separator]);

  /**
   * The teardown path's window on the flow (resolvePendingReviewOnTeardown, below).
   *
   * These three are assigned DURING RENDER — the shipped shape for this file (hostRef,
   * generatedPromptRef) — which is safe for values the teardown reads as context. It would NOT be
   * safe for the "is there anything left to resolve" gate: handleDiscard and the board dialog's own
   * close run inside one batch, so a cleared value would never reach a commit and the teardown
   * would re-apply a generation that had just been discarded and refunded. That gate lives in
   * itemsRef + applyBusyRef instead, which are only ever written imperatively.
   */
  const createAddAsNewShotRef = useRef(createAddAsNewShot);
  createAddAsNewShotRef.current = createAddAsNewShot;
  /**
   * The deferred creator, read the same way (see AiWorkspaceFlowHost.onCreateShotForApply).
   *
   * A REF and not a dependency because the three paths that consult it are all callbacks with
   * deliberately narrow dep lists — handleCancel's is empty, the teardown's is empty, and
   * widening either would rebuild the callback (and, for the teardown, re-run its unmount effect)
   * on every render of a host that re-creates the closure. Its truthiness is a mount-time property
   * of the host, so a stale read is not reachable: a dialog never becomes deferred half way
   * through, it is mounted deferred or it is not.
   */
  const deferredCreateRef = useRef(hostOpts?.onCreateShotForApply);
  deferredCreateRef.current = hostOpts?.onCreateShotForApply;
  const generationOriginRef = useRef(generationOrigin);
  generationOriginRef.current = generationOrigin;
  const stepRef = useRef(step);
  stepRef.current = step;
  /** True for the whole Apply round trip. Written beside every setApplyBusy, never during render. */
  const applyBusyRef = useRef(false);
  /**
   * The shot a half-finished Apply already created — the insert landed and the media write threw.
   * A retry (and the teardown) put the media on THAT shot rather than inserting a second one.
   * Cleared on every fresh generation and on every completed Apply/Discard.
   */
  const createdShotIdRef = useRef<number | null>(null);
  /**
   * The shot the LAST "Add as new shot" Apply put its media on, or null (20260925, owner item 15a).
   * Written right before that Apply's finish() and reset at the start of every Apply, so a stale id
   * can never outlive the press that produced it. handleApply stays a boolean (see its contract);
   * this is how a host that closes itself after the Apply learns WHICH shot to hand back
   * (AiWorkspace.tsx's closeAfterApply belt-and-braces select).
   */
  const lastAppliedShotIdRef = useRef<number | null>(null);

  const hasImage = (s: TimelineShot) => !!s.thumbnailDataUrl;
  const isStaticShot = (s: TimelineShot) => !!s.historyId || !!s.hasStaticImage;

  const approvals = useCommentStore(s => s.approvals);
  /** Shot IDs with approved status — skipped when skipApproved is on */
  const approvedShotIds = useMemo(() => {
    const ids = new Set<number>();
    for (const a of approvals) {
      if (a.status === 'approved') ids.add(a.shotId);
    }
    return ids;
  }, [approvals]);

  /** True when the project has any thumbnail-bearing shots with AI enhancements or static images */
  const hasStaticShotsInProject = useMemo(() =>
    allScenes.flatMap(s => s.shots).some(sh => hasImage(sh) && isStaticShot(sh)),
    [allScenes],
  );

  const scopeShots = useMemo((): TimelineShot[] => {
    if (!project) return [];
    if (task === 'continuous') {
      if (!initialPair) return []; // No pair selected yet — grid tap will set it
      const shots = allScenes.flatMap(s => s.shots);
      const from = shots.find(s => s.id === initialPair.fromShotId);
      const to = shots.find(s => s.id === initialPair.toShotId);
      return [from, to].filter((s): s is TimelineShot => !!s && hasImage(s));
    }
    const shouldSkipApproved = skipApproved;
    switch (scope) {
      case 'all':
        return allScenes.flatMap(s => s.shots.filter(sh =>
          hasImage(sh)
          && (overwriteExisting || !isStaticShot(sh))
          && (!shouldSkipApproved || !approvedShotIds.has(sh.id))
        ));
      case 'selectedScenes': {
        const selectedSceneIdSet = new Set(
          allScenes.filter(s => s.shots.some(sh => selectedShotIds.has(sh.id))).map(s => s.id)
        );
        return allScenes
          .filter(s => selectedSceneIdSet.has(s.id))
          .flatMap(s => s.shots.filter(sh =>
            hasImage(sh) && (!shouldSkipApproved || !approvedShotIds.has(sh.id))
          ));
      }
      case 'selectedShots':
        // hasImage is bypassed for text-only: the shot's picture is not an input, so a shot that
        // has never had one (a brand-new shot the user wants to fill with a generated frame) must
        // still reach the generator. Deliberately narrowed to this explicitly-selected branch —
        // relaxing it in 'all' would sweep every empty shot in the project into a batch.
        //
        // A Create run is text-only by construction but does NOT use the shared `textOnly` flag
        // (it would collide with the Enhance tab's prompt custody), so it relaxes the same gate
        // through its origin instead. Narrow in the same way: generationOrigin is only 'create'
        // between handleCreateGenerate and the return to options, and only in this scope.
        //
        // Third relaxation, same shape: a videoTextOnly host's VIDEO task also generates from a
        // plate, so a never-imaged shot must reach it too. Without this the scope comes out empty,
        // scopeUnits with it, and the dialog shows a live-looking Generate quoting ~0cr that does
        // nothing. Still inside 'selectedShots' — 'all' is not widened.
        return allScenes
          .flatMap(s => s.shots)
          .filter(sh => selectedShotIds.has(sh.id)
            && (textOnly || generationOrigin === 'create' || (videoTextOnly && task === 'video') || hasImage(sh)));
    }
  }, [scope, allScenes, project, task, initialPair, overwriteExisting, skipApproved, approvedShotIds, selectedShotIds, textOnly, generationOrigin, videoTextOnly]);

  // Build generation units — for continuous, create a single first/last pair
  const scopeUnits = useMemo((): GenerationUnit[] => {
    if (isEnhance) return scopeShots.map(shot => ({ type: 'shot' as const, shot }));
    if (task === 'continuous' && initialPair && scopeShots.length >= 2) {
      return [{ type: 'continuous', fromShot: scopeShots[0], toShot: scopeShots[1] }];
    }
    return scopeShots.map(shot => ({ type: 'shot' as const, shot }));
  }, [scopeShots, task, initialPair, isEnhance]);

  // ── Continuous availability ────────────────────────────────────────────
  // First/Last Shot Video requires at least 2 shots in the project.
  // Scope is reset on selection so the current shot doesn't matter.
  // All shots get thumbnails on Timeline entry, so this is a sanity check.
  const continuousAvailable = useMemo(() =>
    allScenes.flatMap(s => s.shots).length >= 2,
    [allScenes],
  );

  // ── Prompt ────────────────────────────────────────────────────────────
  // generatedPrompt is base-only (metadata handled in separate editable field)
  const generatedPrompt = useMemo(() => {
    if (isEnhance) {
      return STYLE_PRESET_PROMPTS[stylePreset];
    }
    // A videoTextOnly host has no generated video prompt to offer: the box is the user's shot
    // description and DEFAULT_VIDEO_PROMPT's "animate the scene from the source image" would be
    // actively wrong over a flat gray plate. Empty, not merely unused, so nothing downstream
    // (handlePromptReset, the seeding effect) can put it back.
    if (videoTextOnly && task === 'video') return '';
    if (scopeShots.length === 0 || !project) return DEFAULT_VIDEO_PROMPT;
    if (task === 'continuous' && scopeShots.length >= 2) {
      const scene = allScenes.find(s => s.id === scopeShots[0].sceneId);
      if (scene) return buildTransitionVideoPrompt(scopeShots[0], scopeShots[1], scene, false);
    }
    const shot = scopeShots[0];
    const scene = allScenes.find(s => s.id === shot.sceneId);
    if (!scene) return DEFAULT_VIDEO_PROMPT;
    const ctx = buildPromptContext(shot, scene, null);
    return buildVideoPrompt(ctx, false);
  }, [task, isEnhance, scopeShots, allScenes, project, stylePreset, videoTextOnly]);
  // Read by the task force-back effect above, which runs before this render's value could reach it
  // through a dependency list. Assigned during render on purpose — same reason hostRef exists.
  generatedPromptRef.current = generatedPrompt;

  // ── Metadata suffix (editable) ────────────────────────────────────────
  const [editableMetadata, setEditableMetadata] = useState('');

  const computedMetadata = useMemo(() => {
    if (scopeShots.length === 0 || !project) return '';
    const shot = scopeShots[0];
    const scene = allScenes.find(s => s.id === shot.sceneId);
    if (!scene) return '';
    const ctx = buildPromptContext(shot, scene, null);
    return buildMetadataSuffix(ctx);
  }, [scopeShots, allScenes, project]);

  // Sync editable metadata when computed value changes (shot/scene change)
  // Skip sync when scope is 'all' — metadata is per-shot automatic, not user-editable
  const prevComputed = useRef(computedMetadata);
  useEffect(() => {
    if (scope !== 'all' && aiUseMetadata && computedMetadata !== prevComputed.current) {
      setEditableMetadata(computedMetadata);
    }
    prevComputed.current = computedMetadata;
  }, [aiUseMetadata, computedMetadata, scope]);

  // Initialize metadata when toggled on (don't clear on uncheck — UI grays it out)
  const prevMetadataOn = useRef(aiUseMetadata);
  useEffect(() => {
    if (aiUseMetadata && !prevMetadataOn.current && !editableMetadata) {
      setEditableMetadata(computedMetadata);
    }
    prevMetadataOn.current = aiUseMetadata;
  }, [aiUseMetadata, computedMetadata, editableMetadata]);

  const metadataSuffix = editableMetadata;

  // ── Cost ──────────────────────────────────────────────────────────────
  const costEstimate = useMemo(() => {
    if (isEnhance) return estimateCost(scopeShots.length, imageModelId, catalog);
    // Per-second video pricing: each unit costs rate × its own (clamped) duration.
    const unitCost = (u: GenerationUnit) =>
      getModelCost(videoModelId, catalog, u.type === 'continuous' ? u.fromShot.duration : u.shot.duration);
    const totalTokens = scopeUnits.reduce((sum, u) => sum + unitCost(u), 0);
    const perShot = scopeUnits.length > 0 ? Math.round(totalTokens / scopeUnits.length) : getModelCost(videoModelId, catalog);
    return { perShot, totalShots: scopeUnits.length, totalTokens, formatted: '' };
  }, [isEnhance, scopeShots.length, scopeUnits, imageModelId, videoModelId, catalog]);

  const estTotalSeconds = useMemo(() => {
    if (isEnhance) {
      const perShot = getModelEstSeconds(imageModelId, catalog);
      const model = catalog.find(m => m.id === imageModelId);
      if (model?.mode === 'sync' && scopeShots.length > 1) {
        return Math.ceil(scopeShots.length / 2) * perShot;
      }
      return scopeShots.length * perShot;
    }
    const perUnit = getModelEstSeconds(videoModelId, catalog);
    return scopeUnits.length * perUnit;
  }, [isEnhance, scopeShots.length, scopeUnits.length, imageModelId, videoModelId, catalog]);

  /**
   * Aspect for the text-only seed plate.
   *
   * The server currently hardcodes the OUTPUT aspect (`aspect_ratio:"16:9"` /
   * `image_size:"landscape_16_9"` in the edge function) and overrides whatever the plate carries,
   * so on a 2.35:1 project this changes nothing today. It is still the correct value to compute at
   * the client layer, and it becomes right the moment the edge function accepts an aspect — do not
   * "simplify" it back to a hardcoded 16:9.
   */
  const seedAspect = useMemo(
    () => parseAspectRatio(project?.aspectRatio ?? '') || DEFAULT_SEED_ASPECT,
    [project?.aspectRatio],
  );

  const durationLimits = getDurationLimits(activeModelId, catalog);
  const hasClamping = useMemo(() =>
    !isEnhance && scopeShots.some(s => Math.round(s.duration) !== clampDuration(s.duration, activeModelId, catalog)),
    [isEnhance, scopeShots, activeModelId, catalog]
  );

  // ── Lifecycle ─────────────────────────────────────────────────────────
  // Load catalog when workspace opens
  useEffect(() => {
    if (open) useAiStore.getState().loadModelCatalog();
  }, [open]);

  // Mirror this hook's local step into tutorialStore.renderFlowStep. TutorialRail is mounted
  // separately (SceneBoardPage, not this hook's component tree) and has no other read path to
  // the options/generating/review/done sub-state it needs for the render-beat arrows. Mirrored
  // unconditionally (not gated on tutorial-active) — it's cheap state, and gating it would let it
  // go stale the moment a tour starts mid-session with the workspace already open. Cleared on
  // unmount (this hook has TWO call sites since 2026-08-21 — AiWorkspace.tsx and the board's
  // BoardEnhanceDialog — but SceneBoardPage's route ternary unmounts TimelineView on the board
  // route, so at most one is ever alive and unmount still == that host closing. Two live
  // instances would fight over renderFlowStep and double every close handler; keep them
  // structurally exclusive.)
  useEffect(() => {
    useTutorialStore.getState().setRenderFlowStep(step);
  }, [step]);
  useEffect(() => () => {
    useTutorialStore.getState().setRenderFlowStep(null);
  }, []);

  // Timer during generating step
  useEffect(() => {
    if (step !== 'generating') { setElapsedSeconds(0); return; }
    const iv = setInterval(() => setElapsedSeconds(prev => prev + 1), 1000);
    return () => clearInterval(iv);
  }, [step]);

  // Initialize prompt + metadata on first open / task change.
  //
  // In practice this re-seeds on EVERY generatedPrompt change, not only the first: the task effect
  // below runs setPi(false) on mount too, in the same flush that sets it true, so promptInitialized
  // settles at false. That is the shipped behaviour and the Timeline depends on it — but it means
  // this, and not only handleStylePresetChange, is a writer of the textarea. Text-only mode must be
  // excluded from BOTH: there the box holds the user's own description of the shot.
  //
  // This is ALSO the effect a Create run would clobber the Enhance textarea through, and the reason
  // the suppression is checked here rather than only in the task effect: a task change moves
  // `generatedPrompt`, and promptInitialized is already false at rest, so this re-seeds on the dep
  // change alone without the task effect having to do anything. Read, never cleared — the task
  // effect below owns the one-shot's lifetime, and it runs later in this same flush.
  useEffect(() => {
    if (suppressTaskPromptReseedRef.current) return;
    // Same exemption as the force-back effect above: on a videoTextOnly host the video box belongs
    // to the user. generatedPrompt is '' there, so without this the seeding pass would not re-seed
    // it — it would ERASE it.
    if (videoPromptIsUserOwnedRef.current) return;
    if (open && !promptInitialized && !textOnlyRef.current) {
      // A mount that carried an Enhance prompt hand-off seeds THAT, and keeps seeding it until
      // generatedPrompt itself moves — see enhanceHandoffSeedRef for why the obvious
      // release-on-first-pass is wrong. Once released, every seed is generatedPrompt exactly as
      // before this existed.
      if (enhanceHandoffRef.current !== null) {
        if (enhanceHandoffSeedRef.current === null) enhanceHandoffSeedRef.current = generatedPrompt;
        else if (enhanceHandoffSeedRef.current !== generatedPrompt) enhanceHandoffRef.current = null;
      }
      setEditablePrompt(enhanceHandoffRef.current ?? generatedPrompt);
      if (computedMetadata) setEditableMetadata(computedMetadata);
      setPi(true);
    }
  }, [open, generatedPrompt, computedMetadata, promptInitialized]);

  // (metadata toggling no longer resets prompt — metadata is a separate field)

  // Reset when workspace opens/closes
  const storePreSelectedShotId = useUiStore(s => s.aiWorkspacePreSelectedShotId);
  const prevOpenRef = useRef(false);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (open && !wasOpen) {
      // Just opened. A host-supplied id is owned by the host (the board dialog holds it for as
      // long as it is up), so unlike the store's parked one it is NOT consumed here.
      const hostPreSelectedShotId = hostRef.current?.preSelectedShotId;
      const preSelectedShotId = hostPreSelectedShotId ?? storePreSelectedShotId;
      if (preSelectedShotId != null) {
        // Opened with a pre-selected shot (e.g. from timeline contextual menu)
        setScope('selectedShots');
        setSelectedShotIds(new Set([preSelectedShotId]));
        // Clear so it doesn't re-apply on re-renders
        if (hostPreSelectedShotId == null) useUiStore.setState({ aiWorkspacePreSelectedShotId: null });
      } else if (hostRef.current?.host === 'boardDialog') {
        // The board has no playhead, and the dialog always opens against one shot — reaching
        // here means it opened with no target at all, so select nothing. Asserted rather than
        // assumed: the fallback below would otherwise seed a Timeline shot the user never
        // pointed at, and the dialog would enhance it.
        setScope('selectedShots');
        clearSelection();
      } else if (useUiStore.getState().aiWorkspaceTab === 'create') {
        // 20260924 (owner): the Timeline's AI Studio gutter button now lands on ENHANCE (AiGutterButton
        // names 'stylegrade' before opening, like the other Timeline entries), so the TIMELINE bullet
        // below describes a Create landing no live Timeline caller makes any more; the board's unscoped
        // row is this arm's only live caller today. The rule is kept for any future Create opener.
        // T1+T2 (owner 2026-09-01): a CREATE landing with no explicit target must not invent one.
        // Create needs a shot only as a write target, so seeding the playhead's shot silently
        // points a generate at a frame the user never chose — which is exactly what the board's
        // "unscoped" AI Studio row and the Timeline gutter were doing. Instead mirror what the
        // surface the user came FROM actually SHOWS as selected, and otherwise select NOTHING.
        // Same assertion the boardDialog arm above makes, for the same reason.
        // The tab is read through getState() so the deps below stay [open, clearSelection].
        // The playhead seed in the final arm stays for the Enhance/Video landings (Alt+I / Alt+V /
        // the continuous pair / the tutorial gutter), which all name 'stylegrade' before this runs.
        setScope('selectedShots');
        // WHICH surface opened this decides whether there is anything to mirror, and the two
        // surfaces disagree about what "selected" looks like:
        //  • BOARD (aiWorkspaceReturnToBoard — written only by enterAiWorkspaceFromBoard and
        //    cleared on every close path; its ONLY no-id caller is SceneBoardPage's no-selection
        //    "AI Studio" row) ⇒ mirror NOTHING. That row exists only while the panel is showing
        //    ProjectActionsView, i.e. while the board is telling the user nothing is selected.
        //    20260902 — the two now AGREE on a permanent-panel layout: a cold selection is the
        //    panel's shot there (useShotActionsPanelOpen), so ProjectActionsView means the panel
        //    has no shot to show — selectedShotId == null, or a selection the board rows no longer
        //    resolve (a deleted shot) — and this arm mirrors the same nothing the board shows. Before
        //    that ruling the panel's gate ALSO demanded userInitiatedShotSelection, while Unity's
        //    initialSceneId hand-off, setActiveScene and usePlayback all select programmatically —
        //    so the row sat over a very-much-non-null id and mirroring it put the owner's reported
        //    first shot straight back. The rule is kept as the guard for any future no-id caller,
        //    and for the layouts where the panel is not permanent.
        //  • TIMELINE (the AI gutter) ⇒ mirror projectStore.selectedShotId: there it IS the visible
        //    ShotLane/ShotBar highlight, the honest answer to "what does the user think is
        //    selected". userInitiatedShotSelection is NOT usable as the test on this side — ShotBar
        //    selects non-user-initiated by design, so it would clear a highlight the user can see.
        const openedFromBoard = useUiStore.getState().aiWorkspaceReturnToBoard;
        const visibleShotId = openedFromBoard ? null : useProjectStore.getState().selectedShotId;
        // Resolved through projectStore.allShots() — the same omitted-scene-filtered, project-
        // ordered list the playhead arm below walks (getShotAtTime), and deliberately STRICTER
        // than this hook's own allShotsFlat (built from project.scenes UNFILTERED): a stale id, or
        // one inside an omitted scene, degrades to nothing rather than to a phantom target.
        const visibleShot = visibleShotId != null
          ? useProjectStore.getState().allShots().find(s => s.id === visibleShotId) ?? null
          : null;
        if (visibleShot) {
          setSelectedShotIds(new Set([visibleShot.id]));
        } else {
          clearSelection();
          // …and the empty selection must STAY empty when the user tabs over to Enhance.
          // StyleGradeTab auto-flips an empty 'selectedShots' scope to ALL SHOTS while
          // autoScopeSwitch is true (StyleGradeTab.tsx:165-170), so without this a landing the
          // user opened with nothing selected would arm a whole-project batch Generate one tab
          // click later — the failure mode useAiWorkspaceFlow.host.test.ts's header calls the
          // most dangerous in the feature. This is exactly handleScopeChange's "the user picked
          // Selected Shots, let an empty selection stand" semantics; picking any scope from the
          // dropdown re-arms it.
          setAutoScopeSwitch(false);
        }
      } else {
        // Default to "Selected Shots" with current shot auto-highlighted
        setScope('selectedShots');
        const currentShotAtPlayhead = useProjectStore.getState().getShotAtTime(
          usePlaybackStore.getState().currentTime
        );
        if (currentShotAtPlayhead) {
          setSelectedShotIds(new Set([currentShotAtPlayhead.id]));
        } else {
          clearSelection();
        }
      }
      setTouchUpTargetShotId(null);
      setCreateTargetShotId(null);
    }
    // There is deliberately NO `!open && wasOpen` arm here. Both hosts mount this hook's component
    // conditionally on the very flag it watches (TimelineView.tsx renders <AiWorkspace/> only while
    // aiWorkspaceOpen; the board dialog passes a literal `open: true` for its whole lifetime), so a
    // close re-renders the PARENT first and unmounts the child before any child effect could see
    // open=false. Everything that used to live in that arm has moved (2026-08-25):
    //   • the pending-review apply  → resolvePendingReviewOnTeardown, in the unmount cleanup below,
    //     which is the only hook that actually fires on a close;
    //   • abortRef.current?.abort() → already duplicated by the unmount cleanup further down;
    //   • setGeneratingImage/Video(null) → the same teardown cleanup (they are GLOBAL store writes,
    //     the only two in that arm that outlive the component);
    //   • every setState reset (step/progress/items/selection/prompt flags) → dropped as moot: the
    //     next open is a fresh mount with fresh initial state.
  }, [open, clearSelection]);

  /**
   * Resolve a review the user walked away from.
   *
   * The generation is already PAID FOR by the time the review step appears, so closing the surface
   * must not silently throw it away. This is the shipped "whatever finished gets applied" contract
   * — it simply never ran until now, because the close branch it lived in was unreachable (see the
   * note above), which made every close during review a silent loss.
   *
   * Everything it needs is read through refs and handed to MODULE-LEVEL functions as plain values:
   * this runs during unmount, so nothing may touch hook state, and the work continues after the
   * component is gone. Errors go to diagLog, never to a dialog — the surface that would show one no
   * longer exists.
   *
   * Deliberate non-goals, all of them shipped behaviour:
   *   • VIDEO items are not auto-applied. attachVideo is a visible, retimeable edit; the close-path
   *     contract has always covered finished IMAGES only.
   *   • Rejected items are neither applied nor refunded. Refunds belong to Discard, which the user
   *     did not press.
   */
  const resolvePendingReviewOnTeardown = useCallback(() => {
    // Only a LIVE review is this path's business. 'generating' in particular belongs to the abort
    // path — the unmount cleanup below aborts, and the batch's own catch applies whatever finished
    // (handleEnhance's `if (abort.signal.aborted)` arm) — so acting here too would double-apply.
    if (stepRef.current !== 'review') return;
    // Apply already in flight — insertShotAndApplyMedia is module-level and is still running. It
    // owns the outcome; a second chain here would insert a second shot.
    if (applyBusyRef.current) return;
    // An IMPORT review is the one kind this contract does not cover. The rationale above is
    // "already PAID FOR", and an imported file cost nothing: the user dropped a picture, was shown
    // a before/after, and closed the surface without pressing Apply. That is a DISCARD. Applying it
    // anyway would overwrite a frame on the way out of a decision the user declined to make.
    // Read through the render-assigned ref like the other teardown context — and note the stale
    // direction is the safe one here: a value left at 'import' can only ever skip an apply.
    if (generationOriginRef.current === 'import') return;

    const pending = itemsRef.current.filter(
      (i): i is ImageReviewItem => i.kind === 'image' && i.success && i.accepted && !!i.enhancedDataUrl,
    );
    if (pending.length === 0) return;
    // Taking ownership: a StrictMode double-invoke of this cleanup must not apply twice.
    itemsRef.current = [];

    /**
     * A DEFERRED run (the board's Add-shot chooser) counts as "add as new shot" here whatever its
     * origin is — it is an 'enhance' origin by construction, because the board dialog's Create arm
     * routes through handleEnhance with textOnly, not through handleCreateGenerate.
     *
     * AND THIS PATH DOES CREATE, unlike handleCancel and the abort catch below (20260828, owner):
     * a teardown is not a user decision. The surface was taken down — Unity closed the panel, the
     * route changed — with a paid, completed image in hand, and the shipped contract for that is
     * "whatever finished gets applied". Dropping it here would lose money the user never chose to
     * spend a second time. A user who actively backs out reaches handleDiscard instead, which
     * refunds; that is the difference the two behaviours encode.
     */
    const asNewShot = (deferredCreateRef.current != null
      || (generationOriginRef.current === 'create' && createAddAsNewShotRef.current))
      && capabilities.bridge
      && !isReadOnly();

    if (!asNewShot) {
      applyBatchGenerations(pending.map(item => ({
        shotId: item.shotId,
        enhancedDataUrl: item.enhancedDataUrl!,
        historyId: item.historyId!,
      })));
      return;
    }

    // "Add as new shot": applying to the anchor would overwrite a frame the user never pointed at,
    // so the close still goes through Unity's create. Single item by construction — a Create run
    // generates exactly one.
    const first = pending[0];
    const media: CreatedShotMedia = {
      enhancedDataUrl: first.enhancedDataUrl!,
      historyId: first.historyId!,
    };
    const alreadyCreated = createdShotIdRef.current;
    const deferredCreate = deferredCreateRef.current;
    const chain = alreadyCreated != null
      ? applyMediaToShot(alreadyCreated, media)
      : deferredCreate
        // The deferred twin of insertShotAndApplyMedia. Inlined rather than given its own
        // module-level helper because the creator is already a plain, component-free callback the
        // host handed in — there is no hook state to keep out of it, which is the only reason
        // insertShotAndApplyMedia exists at module scope. A null id means the create failed and
        // was already reported by the board; there is nothing left to apply it to.
        ? deferredCreate().then((id) => (id == null ? undefined : applyMediaToShot(id, media)))
        : insertShotAndApplyMedia(
          first.shotId, media,
          (msg) => diagLog('AiGen', `teardown insert failed: ${msg}`),
        );
    void chain.catch((err) => diagLog(
      'AiGen',
      `teardown apply failed: ${err instanceof Error ? err.message : String(err)}`,
    ));
  }, []);

  // The ONE hook that fires on a close (see the open-effect note). Empty deps on purpose: it must
  // run at unmount and nowhere else, and every value it needs is behind a ref.
  useEffect(() => () => {
    resolvePendingReviewOnTeardown();
    // Global store state, so unlike the rest of the old reset block these two outlive the
    // component. The generate handlers' own `finally` normally clears them; this is the backstop
    // for a close that beats the abort rejection (and for runBakedGeneration, which never sets
    // them but is free to clear them).
    useAiStore.getState().setGeneratingImage(null);
    useAiStore.getState().setGeneratingVideo(null);
  }, [resolvePendingReviewOnTeardown]);

  // Re-initialize prompt when task changes — unless the change was Create mirroring its own
  // dropdown, which is presentation state and must not touch the Enhance tab's textarea. The
  // one-shot is consumed HERE, unconditionally: this effect runs on every task change (the
  // seeding effect above only reads it), so clearing anywhere else could strand the flag and
  // swallow the next genuine, user-driven task change.
  useEffect(() => {
    const suppressed = suppressTaskPromptReseedRef.current;
    suppressTaskPromptReseedRef.current = false;
    if (suppressed) return;
    if (open) {
      setPi(false);
    }
  }, [task]);

  // Cleanup abort on unmount. Also the "is this hook still alive" flag the animatic pre-flight in
  // handleEnhance checks after its await (20260903, reviewer): the confirm is a global dialog that
  // outlives its host, so a host torn down UNDER it (Unity's hardware Escape, a route write) must
  // not be resumed into — the abort controller that would cancel the run is created only AFTER
  // this cleanup has run, so a headless paid generation would otherwise follow the revert. Reset
  // on (re)mount so Strict Mode's mount→cleanup→mount cannot leave it stuck true.
  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => { unmountedRef.current = true; abortRef.current?.abort(); };
  }, []);

  // ── Scope selector data ───────────────────────────────────────────────
  const sceneOptions = useMemo(() =>
    allScenes.map(s => ({
      id: s.id,
      number: s.number,
      shotCount: s.shots.filter(hasImage).length,
    })),
    [allScenes],
  );

  const allShotCount = useMemo(() =>
    allScenes.flatMap(s => s.shots).filter(hasImage).length,
    [allScenes],
  );

  const continuousPairCount = useMemo(() =>
    scopeUnits.filter(u => u.type === 'continuous').length,
    [scopeUnits]
  );

  // ── Refund + history cleanup helper ──────────────────────────────────

  const refundAndCleanup = useCallback((items: ReviewItem[]) => {
    const totalCost = items.reduce((sum, i) => sum + (i.tokenCost ?? 0), 0);
    const historyIds = items.map(i => i.historyId).filter((id): id is string => !!id);

    if (totalCost > 0) {
      import('@/api/aiApi.ts').then(({ requestRefund }) => {
        requestRefund(totalCost).catch(err => console.warn('Refund failed:', err));
      });
    }
    if (historyIds.length > 0) {
      useAiStore.getState().removeHistoryEntries(historyIds);
    }
  }, []);

  // ── Actions ───────────────────────────────────────────────────────────

  const handleScopeChange = useCallback((newScope: AiWorkspaceScope, sceneId?: number) => {
    setScope(newScope);
    setSelectedSceneId(sceneId ?? null);
    if (newScope === 'selectedShots') {
      // Manual dropdown pick — disable auto-switch so empty selection stays
      setAutoScopeSwitch(false);
    } else if (newScope === 'selectedScenes') {
      setAutoScopeSwitch(true);
      // Keep existing selection if any; user will tap scenes in the grid
    } else {
      setAutoScopeSwitch(true);
      clearSelection();
    }
    if (newScope !== 'all') {
      // Restore per-shot metadata when switching to single-shot/scene scope
      setEditableMetadata(computedMetadata);
    }
  }, [computedMetadata, clearSelection]);

  /** Switch task — clears pair and selection so the new mode starts fresh. */
  const handleTaskChange = useCallback((newTask: AiWorkspaceTask) => {
    const currentTask = useUiStore.getState().aiWorkspaceTask;
    if (currentTask === 'continuous' && newTask !== 'continuous' && initialPair) {
      // Leaving continuous — keep only the user-selected shot, drop the auto-paired one
      setSelectedShotIds(new Set([initialPair.fromShotId]));
      setScope('selectedShots');
      setAutoScopeSwitch(true);
    } else if (newTask === 'continuous' && selectedShotIds.size > 1) {
      // Entering continuous with too many selected — clear
      setSelectedShotIds(new Set());
    }
    useUiStore.setState({
      aiWorkspaceTask: newTask,
      aiWorkspaceInitialPair: null,
    });
  }, [selectedShotIds, initialPair]);

  const handlePromptReset = useCallback(() => {
    // An explicit "give me the generated prompt back" — so the hand-off's tenure is over, whether or
    // not generatedPrompt moves (here it never does: this writes the current one).
    enhanceHandoffRef.current = null;
    setEditablePrompt(generatedPrompt);
  }, [generatedPrompt]);

  const handleStylePresetChange = useCallback((preset: StylePreset) => {
    // Same gesture, same reason — and RE-TAPPING THE ACTIVE PRESET is why this cannot be left to the
    // generatedPrompt-move release: neither tab's preset buttons guard on the active preset, so the
    // handler runs, writes the same string, and generatedPrompt is value-identical.
    enhanceHandoffRef.current = null;
    setStylePresetState(preset);
    localStorage.setItem('previs-style-preset', preset);
    // Text-only: the textarea holds the USER's description of the shot, and the preset prompts are
    // image-to-image boilerplate — writing one here would silently destroy what they typed. Since
    // 2026-08-21 the preset contributes nothing at all in this mode (the typed prompt is the sole
    // authority over the look), and the board dialog disables the buttons; this stays as the guard
    // for any other caller. The overwrite is untouched for enhance mode, which the Timeline drives.
    if (textOnly) return;
    setEditablePrompt(STYLE_PRESET_PROMPTS[preset]);
  }, [textOnly]);

  /**
   * The "Include image" checkbox, inverted (checked = include = textOnly false).
   *
   * The prompt is the delicate part. Unchecking must clear the seeded preset — "@image1",
   * "Maintain 1:1 spatial blocking", "Replace stock avatars with … figures in the same exact
   * coordinates" are incoherent with no input image, and that last clause would put PEOPLE in an
   * empty skyline panorama. But it must never clear text the user typed, in either direction.
   */
  const setTextOnly = useCallback((next: boolean) => {
    setTextOnlyState(next);
    setEditablePrompt(prev => {
      if (next) return stripPresetBoilerplate(prev);
      // Re-checking refills only when there is nothing to lose.
      return prev.trim() === '' ? STYLE_PRESET_PROMPTS[stylePreset] : prev;
    });
  }, [stylePreset]);

  const startGeneration = useCallback((): AbortController => {
    setStep('generating');
    completedMediaRef.current = new Map();
    setCompletedMedia(new Map());
    setProgress(null);
    itemsRef.current = [];
    // A fresh run cannot inherit the shot a previous half-finished Apply created.
    createdShotIdRef.current = null;
    const abort = new AbortController();
    abortRef.current = abort;
    return abort;
  }, []);

  // ── Import: drop straight into review (2026-08-25) ─────────────────────

  /**
   * Park a dropped file in the ordinary generation review, targeting `shot`.
   *
   * The Import tab used to apply on a tap-a-shot confirm popup, with no before/after and no way
   * back. It now produces the SAME review the generators do — one item, Discard + Apply — which is
   * why this deliberately reuses the shared step machine instead of growing a second review
   * surface: a standalone one would sit outside itemsRef/stepRef/applyBusyRef and the unmount
   * teardown could not see it at all.
   *
   * Everything here is free. No startGeneration (there is no run to abort and no progress step to
   * show), no tokenCost (refundAndCleanup sums to 0 and skips the refund call), and historyId ''
   * — the Import convention since the original panel, which keeps the in-store value a string and
   * lands `history_id = ''` in Unity rather than a dangling FK. It also means refundAndCleanup's
   * `!!id` filter drops it, so Discard removes no history entry that never existed.
   */
  const startImportReview = useCallback((shot: TimelineShot, dataUrl: string) => {
    // MANDATORY, not cosmetic. WorkspaceMain's review chrome, WorkspaceReview's before/after grid
    // and handleApply's two arms ALL branch on the shared uiStore task (isEnhance), never on the
    // active tab — so an image item dropped while the Enhance pill still says Video renders through
    // the VIDEO arm (no before pane, "reviewFailed" after) and is silently dropped on Apply.
    // Mirrored with the same one-shot the Create tab uses, because a real task change re-seeds
    // editablePrompt and would wipe whatever is typed on the Enhance tab.
    if (useUiStore.getState().aiWorkspaceTask !== 'enhance') {
      suppressTaskPromptReseedRef.current = true;
      useUiStore.setState({ aiWorkspaceTask: 'enhance' });
    }

    // Same single-shot forcing the generate handlers do, so every downstream count is 1.
    setScope('selectedShots');
    setSelectedShotIds(new Set([shot.id]));
    // Nothing here can inherit a shot some earlier half-finished Create apply left behind.
    createdShotIdRef.current = null;

    const item: ImageReviewItem = {
      id: `import-${shot.id}-${Date.now()}`,
      kind: 'image',
      shotId: shot.id,
      success: true,
      enhancedDataUrl: dataUrl,
      originalDataUrl: shot.thumbnailDataUrl,
      historyId: '',
      accepted: true,
    };
    setReviewItems([item]);
    itemsRef.current = [item];
    setGenerationOrigin('import');
    setStep('review');
  }, []);

  // ── Tutorial: baked generation (spec §10) ──────────────────────────────
  // Drives the REAL generating chrome (ring, phases, credit line) on a scripted
  // timeline, then lands the pre-baked sketch in the normal review step.
  // submitImageEnhancement is never called: no network, no credits, no history row.
  const runBakedGeneration = useCallback(async (shot: TimelineShot) => {
    const { loadTutorialSketchDataUrl, sortedTutorialShots, FAKE_RENDER_SECONDS } =
      await import('@/components/Tutorial/tutorialConfig');
    const scene = allScenes.find(sc => sc.shots.some(sh => sh.id === shot.id));
    const order = scene ? sortedTutorialShots(scene).findIndex(sh => sh.id === shot.id) : -1;
    const bakedDataUrl = order >= 0 ? await loadTutorialSketchDataUrl(order) : null;
    if (!bakedDataUrl) {
      // A missing baked asset must NEVER fall through to the paid generate path.
      diagLog('AiGen', `runBakedGeneration: no baked sketch for shot ${shot.id} (order ${order}) — tutorial asset missing`);
      return;
    }
    const abort = startGeneration();
    setProgress({ current: 0, total: 1, message: i18next.t('aiWorkspace:preparing') });
    window.setTimeout(() => {
      if (abort.signal.aborted) return;
      const item: ImageReviewItem = {
        id: String(shot.id), kind: 'image', shotId: shot.id, success: true,
        enhancedDataUrl: bakedDataUrl, originalDataUrl: shot.thumbnailDataUrl,
        historyId: '', accepted: true,
      };
      itemsRef.current = [item];
      completedMediaRef.current.set(item.id, bakedDataUrl);
      setCompletedMedia(new Map(completedMediaRef.current));
      setReviewItems([item]);
      setStep('review');
    }, FAKE_RENDER_SECONDS * 1000);
  }, [allScenes, startGeneration]);

  const handleCancel = useCallback(() => {
    const msg = i18next.t('errors:cancelGenerationConfirm');
    if (!window.confirm(msg)) return;
    abortRef.current?.abort();

    // Apply already-completed enhancements to the timeline
    const successItems = itemsRef.current.filter((i): i is ImageReviewItem => i.kind === 'image' && i.success && !!i.enhancedDataUrl);
    if (successItems.length > 0) {
      if (deferredCreateRef.current) {
        // DEFERRED RUN — DROP IT, and refund (20260828, owner item 3, and the whole point of the
        // item). There is no shot to write to: the flow's shot is an ANCHOR borrowed for scope and
        // aspect, so the shipped line below would paint the scene's LAST shot with an image the
        // user was about to put on a NEW one — wrong in every reading of what they pressed. Nor
        // may it create the shot instead: the user actively confirmed a cancel, and answering that
        // with a shot they never asked for is the failure the deferred flow exists to remove.
        // So the paid image is thrown away and the credits go back, which is exactly what the
        // review step's own Discard does. (resolvePendingReviewOnTeardown is the deliberate
        // opposite — see its comment: a teardown is not a user decision.)
        // itemsRef is cleared with it, taking ownership so the unmount teardown cannot then
        // re-apply — and re-applying something just refunded is the worst outcome this hook has.
        itemsRef.current = [];
        createdShotIdRef.current = null;
        refundAndCleanup(successItems);
      } else {
        applyBatchGenerations(successItems.map(item => ({
          shotId: item.shotId,
          enhancedDataUrl: item.enhancedDataUrl!,
          historyId: item.historyId!,
        })));
      }
    }

    useAiStore.getState().setGeneratingImage(null);
    useAiStore.getState().setGeneratingVideo(null);
    setGenerationOrigin(null);
    setStep('options');
  }, [refundAndCleanup]);

  // ── Queue reordering (retry / prioritize) ────────────────────────────

  /** Re-generate a completed shot — removes it from completedMedia so it visually
   *  reverts to pending, then pushes it to the front of the priority queue. */
  const handleRetry = useCallback((shotId: number) => {
    const shot = scopeShots.find(s => s.id === shotId);
    if (!shot) return;
    // Remove from shared media ref AND state so generation callbacks don't restore the old entry
    const key = isEnhance ? String(shotId) : `shot-${shotId}`;
    completedMediaRef.current.delete(key);
    setCompletedMedia(new Map(completedMediaRef.current));
    // Remove previous result from items so it won't be double-applied
    itemsRef.current = itemsRef.current.filter(i => {
      if (i.kind === 'image') return (i as ImageReviewItem).shotId !== shotId;
      if (i.kind === 'video' && (i as VideoReviewItem).unitType === 'shot') return (i as VideoReviewItem).shotId !== shotId;
      return true;
    });
    // Push to front of priority queue
    priorityQueueRef.current.unshift(shot);
    setPreviewShotId(null);
  }, [scopeShots, isEnhance]);

  /** Move a pending (not yet generated) shot to the front of the queue. */
  const handlePrioritize = useCallback((shotId: number) => {
    const shot = scopeShots.find(s => s.id === shotId);
    if (!shot) return;
    priorityQueueRef.current.unshift(shot);
    setPreviewShotId(null);
  }, [scopeShots]);

  /** Touch up a completed shot from the preview overlay — re-queue with a custom prompt
   *  using the last generated image (from completedMedia) as the input. */
  const handleTouchUpFromPreview = useCallback((shotId: number, prompt: string) => {
    const shot = scopeShots.find(s => s.id === shotId);
    if (!shot) return;
    const key = isEnhance ? String(shotId) : `shot-${shotId}`;
    // Capture the generated image BEFORE removing from completedMedia
    const generatedImageUrl = completedMediaRef.current.get(key);
    if (!generatedImageUrl) return; // no image to touch up
    // Remove from completed media (revert grid cell to pending)
    completedMediaRef.current.delete(key);
    setCompletedMedia(new Map(completedMediaRef.current));
    // Remove previous result from items
    itemsRef.current = itemsRef.current.filter(i => {
      if (i.kind === 'image') return (i as ImageReviewItem).shotId !== shotId;
      return true;
    });
    // Store the touch-up override with prompt + the generated image
    touchUpOverridesRef.current.set(shotId, { prompt, imageDataUrl: generatedImageUrl });
    // Push to front of priority queue
    priorityQueueRef.current.unshift(shot);
    setPreviewShotId(null);
  }, [scopeShots, isEnhance]);

  // ── Generate: Touch Up (prompt-based editing) ────────────────────────

  const aiSelectedTouchUpModel = useAiStore(s => s.aiSelectedTouchUpModel);

  const handleTouchUpGenerate = useCallback(async () => {
    if (!effectiveTouchUpShot?.thumbnailDataUrl) return;
    const scene = allScenes.find(s => s.shots.some(sh => sh.id === effectiveTouchUpShot.id));
    if (!scene) return;

    const touchUpModelId = useAiStore.getState().aiSelectedTouchUpModel;
    trackEvent('generate_tapped', {
      model_id: touchUpModelId,
      generation_type: 'touchup',
      scope: 'single',
      batch_size: 1,
    });
    const touchUpStartTime = Date.now();

    // Force single-shot scope so progress UI shows 1 item, not the Style Grade batch
    setScope('selectedShots');
    setSelectedShotIds(new Set([effectiveTouchUpShot.id]));
    setGenerationOrigin('touchup');

    const abort = startGeneration();
    setProgress({ current: 0, total: 1, message: i18next.t('aiWorkspace:preparing') });
    useAiStore.getState().setGeneratingImage(effectiveTouchUpShot.id);

    try {
      const { enhanceSingleShot } = await import('@/ai/aiOrchestrator.ts');
      const sceneInfo = { name: scene.name, locationType: scene.locationType, timeOfDay: scene.timeOfDay };
      const result = await enhanceSingleShot(
        effectiveTouchUpShot, sceneInfo, null, undefined, abort.signal, undefined,
        { useMetadata: false, modelId: 'nano-banana-2', customBasePrompt: touchUpPrompt, useCurrentImage: true },
      );

      trackEvent('generate_succeeded', {
        model_id: touchUpModelId,
        generation_type: 'touchup',
        duration_ms: Date.now() - touchUpStartTime,
        credits_spent: result.tokenCost,
      });

      const item: ImageReviewItem = {
        id: String(effectiveTouchUpShot.id), kind: 'image', shotId: effectiveTouchUpShot.id, success: true,
        enhancedDataUrl: result.enhancedDataUrl, originalDataUrl: effectiveTouchUpShot.thumbnailDataUrl,
        prompt: result.prompt, seed: result.seed, tokenCost: result.tokenCost,
        historyId: result.historyId, accepted: true,
      };
      setReviewItems([item]);
      itemsRef.current = [item];
      setStep('review');
    } catch (err) {
      if (abort.signal.aborted) return;
      useUiStore.getState().showAiError(
        i18next.t('errors:enhancementStoppedTitle'),
        describeAiError(err),
      );
      setGenerationOrigin(null);
      setStep('options');
    } finally {
      useAiStore.getState().setGeneratingImage(null);
    }
  }, [effectiveTouchUpShot, allScenes, touchUpPrompt, startGeneration]);

  // ── Generate: Create (text-only, new shot or selected shot) ───────────

  /**
   * The Create tab's generate. Single shot, straight to the orchestrator — deliberately NOT
   * through enhanceBatch: the batch machinery brings the parallel pool, the priority queue and a
   * pinned progress-key set with it, none of which a one-shot text-only run has any use for.
   *
   * Text-only is passed EXPLICITLY in the request options rather than by flipping the shared
   * `textOnly` state, which belongs to the board dialog's "Include image" checkbox and carries
   * prompt-custody side effects (stripPresetBoilerplate) that would rewrite the Enhance tab's
   * textarea out from under it.
   */
  const handleCreateGenerate = useCallback(async () => {
    const shot = effectiveCreateShot;
    if (!shot || !createPrompt.trim()) return;
    const scene = allScenes.find(s => s.shots.some(sh => sh.id === shot.id));
    if (!scene) return;

    const isImage = createTask === 'enhance';
    const modelId = isImage
      ? useAiStore.getState().aiSelectedImageModel
      : useAiStore.getState().aiSelectedVideoModel;

    trackEvent('generate_tapped', {
      model_id: modelId,
      generation_type: isImage ? 'create_image' : 'create_video',
      scope: 'single',
      batch_size: 1,
    });
    const createStartTime = Date.now();

    // WorkspaceMain's generating/review/apply chrome branches on uiStore.aiWorkspaceTask
    // (flow.isEnhance), not on the active tab — point it at the run being started so the review
    // renders the right media kind and handleApply takes the right arm. Presentation state; the
    // Create dropdown keeps its own copy, so nothing is restored on the way back.
    //
    // It is still a change to the SHARED task, and the prompt effects treat a task change as "the
    // user picked a new task" and re-seed editablePrompt from the new task's generated prompt —
    // wiping whatever is typed on the Enhance tab. Suppressed for this one transition, and only
    // when the value actually moves: setting the flag without a change would strand it and swallow
    // the user's next pill change instead (the effect that clears it is keyed on `task`).
    const mirroredTask: AiWorkspaceTask = isImage ? 'enhance' : 'video';
    if (useUiStore.getState().aiWorkspaceTask !== mirroredTask) {
      suppressTaskPromptReseedRef.current = true;
      useUiStore.setState({ aiWorkspaceTask: mirroredTask });
    }

    // Force single-shot scope so the progress UI shows 1 item, same as Touch Up.
    setScope('selectedShots');
    setSelectedShotIds(new Set([shot.id]));
    setGenerationOrigin('create');

    const abort = startGeneration();
    setProgress({
      current: 0,
      total: 1,
      message: i18next.t('aiWorkspace:createProgressCreatingShot', { shot: createProgressLabel }),
    });
    if (isImage) useAiStore.getState().setGeneratingImage(shot.id);
    else useAiStore.getState().setGeneratingVideo(shot.id);

    try {
      const sceneInfo = { name: scene.name, locationType: scene.locationType, timeOfDay: scene.timeOfDay };
      if (isImage) {
        const { enhanceSingleShot } = await import('@/ai/aiOrchestrator.ts');
        const result = await enhanceSingleShot(
          shot, sceneInfo, null, undefined, abort.signal, undefined,
          {
            useMetadata: false,
            modelId,
            customBasePrompt: buildTextToImagePrompt(createPrompt),
            textOnly: true,
            seedAspect,
          },
        );
        trackEvent('generate_succeeded', {
          model_id: modelId,
          generation_type: 'create_image',
          duration_ms: Date.now() - createStartTime,
          credits_spent: result.tokenCost,
        });
        const item: ImageReviewItem = {
          id: String(shot.id), kind: 'image', shotId: shot.id, success: true,
          enhancedDataUrl: result.enhancedDataUrl, originalDataUrl: shot.thumbnailDataUrl,
          prompt: result.prompt, seed: result.seed, tokenCost: result.tokenCost,
          historyId: result.historyId, accepted: true,
        };
        setReviewItems([item]);
        itemsRef.current = [item];
      } else {
        // KNOWN LIMITATION: there is no text-to-video backend — every fal video endpoint requires
        // an input frame, so textOnly hands the model a flat gray plate and it animates that,
        // guided only by the prompt. See VideoSingleOptions.textOnly.
        const { generateSingleVideo } = await import('@/ai/videoOrchestrator.ts');
        const result = await generateSingleVideo(
          shot, sceneInfo, abort.signal, undefined,
          {
            useMetadata: false,
            modelId,
            customVideoPrompt: createPrompt,
            generateAudio,
            audioHint: aiAudioHints[shot.sceneId] ?? undefined,
            textOnly: true,
            seedAspect,
          },
        );
        trackEvent('generate_succeeded', {
          model_id: modelId,
          generation_type: 'create_video',
          duration_ms: Date.now() - createStartTime,
          credits_spent: result.tokenCost,
        });
        const item: VideoReviewItem = {
          id: `shot-${shot.id}`, kind: 'video', unitType: 'shot', shotId: shot.id, success: true,
          videoUrl: result.videoUrl, sourceThumb: shot.thumbnailDataUrl,
          prompt: result.prompt, seed: result.seed, tokenCost: result.tokenCost,
          // Only the Create arm records it: this is the one arm whose apply can insert a shot, and
          // the insert's repush is what makes the blob unmeasurable (20260909, owner).
          duration: result.duration,
          historyId: result.historyId, accepted: true,
        };
        setReviewItems([item]);
        itemsRef.current = [item];
      }
      setStep('review');
    } catch (err) {
      if (abort.signal.aborted) return;
      useUiStore.getState().showAiError(
        i18next.t(isImage ? 'errors:enhancementStoppedTitle' : 'errors:videoGenerationStoppedTitle'),
        describeAiError(err),
      );
      setGenerationOrigin(null);
      setStep('options');
    } finally {
      useAiStore.getState().setGeneratingImage(null);
      useAiStore.getState().setGeneratingVideo(null);
    }
  }, [
    effectiveCreateShot, allScenes, createPrompt, createTask, createProgressLabel,
    seedAspect, generateAudio, aiAudioHints, startGeneration,
  ]);

  // ── Generate: Enhance ─────────────────────────────────────────────────

  const handleEnhance = useCallback(async () => {
    if (scopeShots.length === 0) return;

    /**
     * BOTH pre-flight confirms are SKIPPED for a DEFERRED run (20260828) — the same ref the abort
     * catch below is gated on, for the same reason: a deferred run HAS NO TARGET SHOT. `scopeShots`
     * is the ANCHOR, borrowed for the cost estimate and the seed aspect only; the run is textOnly,
     * so the anchor's pixels are never uploaded, and Apply writes to a shot Unity has not made yet.
     *
     * So both questions are false statements about this run. "This shot already has an AI
     * enhancement — re-enhance?" describes a shot the run neither reads from nor writes to (and the
     * anchor is the scene's LAST shot, so it is exactly the shot a previous Sketch-with-AI just
     * created — the confirm would fire on the second Sketch in a row, every time). The continuous-
     * transition warning is the same shape: nothing is being replaced, so no transition can break.
     *
     * Worse than the wording: DECLINING returns early and nothing else happens, so the Generate
     * button simply appears dead.
     */
    const deferredRun = deferredCreateRef.current != null;

    const existingCount = scopeShots.filter(shot => shot.historyId).length;
    if (!deferredRun && existingCount > 0) {
      const msg = existingCount === 1
        ? i18next.t('errors:reEnhanceSingleConfirm')
        : i18next.t('errors:reEnhanceMultipleConfirm', { count: existingCount });
      if (!window.confirm(msg)) return;
    }

    // Warn about continuous transition conflicts
    const transitions = project?.transitions ?? [];
    const continuousConflicts = scopeShots.filter(shot =>
      transitions.some(t => t.type === 'continuous' && t.videoObjectUrl && (t.fromShotId === shot.id || t.toShotId === shot.id)),
    );
    if (!deferredRun && continuousConflicts.length > 0) {
      const msg = continuousConflicts.length === 1
        ? i18next.t('errors:continuousConflictSingleConfirm')
        : i18next.t('errors:continuousConflictMultipleConfirm', { count: continuousConflicts.length });
      if (!window.confirm(msg)) return;
    }

    /**
     * ANIMATIC PRE-FLIGHT — owner batch 5, T3
     * (docs/superpowers/specs/2026-09-03-owner-batch-5-design.md §T3).
     *
     * Enhance produces a still. An animatic shot is a still PLUS a rendered mp4 and a run of
     * continuing shots, and the apply path only ever cleared the mp4's web-side URL — so the tile
     * showed the new image unselected and played the old animatic selected, and Unity's next
     * sceneReady put the video back. The two are genuinely incompatible, so the answer is a
     * question, not a silent coercion: confirm, revert, then generate.
     *
     * Third in the pre-flight order and gated on the same `deferredRun` as the two above, for the
     * same reason — a deferred run has no target shot, so it can hold no animatic and the question
     * would be a false statement about the run. Placed here (after both confirms, before
     * startGeneration) so a decline at ANY rung costs nothing: no credits, no revert, no mutation.
     *
     * The revert is fired per shot rather than batched because Unity's verb is per shot and each
     * one repushes; the store writes coalesce in the same tick. It runs BEFORE startGeneration
     * so the animatic is gone before any credit is spent — NOT so the generation sees the
     * reverted shot: `scopeShots` is the array this closure captured before the await, and the
     * run below reads THOSE objects (still carrying isAnimatic / the old videoObjectUrl). That is
     * deliberate and safe only because the orchestrator reads thumbnailDataUrl, historyId and
     * hasStaticImage — the three fields revertAnimaticForEnhance leaves untouched (the 3D poster
     * IS the Enhance source). If the revert ever touches one of those, or the orchestrator ever
     * branches on isAnimatic, re-read the scope from the store here (reviewer, 20260903).
     */
    // Bridge-gated as well (reviewer): the revert IS the Unity verb — without a host nothing removes
    // the continuing shots or the mp4, so the dialog's copy would describe work no one performs.
    // Standalone / online review keep today's behaviour for an animatic shot.
    const animatics = scopeShots.filter(shot => shot.isAnimatic);
    if (!deferredRun && capabilities.bridge && animatics.length > 0) {
      if (!(await requestAnimaticEnhanceConfirm(animatics.length))) return;
      // The host may have unmounted while the dialog was up (see unmountedRef): then there is no
      // review to land in, so do NOT revert and do NOT generate — the user's "Revert and enhance"
      // press on an orphaned dialog must cost nothing.
      if (unmountedRef.current) return;
      animatics.forEach(shot => revertAnimaticForEnhance(shot.id));
    }

    const enhanceScopeMap: Record<AiWorkspaceScope, 'single' | 'scenes' | 'all'> = {
      all: 'all',
      selectedScenes: 'scenes',
      selectedShots: 'single',
    };
    trackEvent('generate_tapped', {
      model_id: imageModelId,
      generation_type: 'image',
      scope: enhanceScopeMap[scope],
      batch_size: scopeShots.length,
    });
    const enhanceStartTime = Date.now();

    // Board dialog is always single-shot scope, so useMetadata is false and this string is sent
    // verbatim — which is exactly why text-only needs its own assembly rather than the preset's.
    // stylePreset is deliberately NOT passed: with no input image the typed prompt owns the look
    // (see buildTextToImagePrompt), which is why the dialog disables Photo/Sketch in this mode.
    const fullPrompt = textOnly
      ? buildTextToImagePrompt(editablePrompt)
      : editablePrompt;
    const abort = startGeneration();
    setGenerationOrigin('enhance');
    priorityQueueRef.current = [];
    useAiStore.getState().setGeneratingImage(scopeShots[0].id);
    diagLog('AiGen', `handleEnhance: starting batch for ${scopeShots.length} shots, model=${imageModelId}, scope=${scope}`);

    try {
      const { enhanceBatch } = await import('@/ai/aiOrchestrator.ts');
      const sceneMap = new Map(
        allScenes.map(s => [s.id, { name: s.name, locationType: s.locationType, timeOfDay: s.timeOfDay }])
      );
      const items: ImageReviewItem[] = [];

      await enhanceBatch({
        shots: scopeShots,
        scenes: sceneMap,
        style: null,
        useMetadata: scope === 'all',
        modelId: imageModelId,
        customBasePrompt: fullPrompt,
        textOnly,
        seedAspect,
        // "Enhancing Shot B…" was ambiguous across scenes — the orchestrator only ever had the bare
        // shot.number. Hand it the same scene-qualified label every other surface shows, as DATA:
        // the message itself stays one wording for every caller (see shotProgressMessage).
        shotLabels: new Map(scopeShots.map(s => [s.id, shotFullLabel(s, allScenes, separator)])),
        abortSignal: abort.signal,
        priorityQueue: priorityQueueRef.current,
        promptOverrides: touchUpOverridesRef.current,
        onProgress: p => setProgress({ current: p.current, total: p.total, message: p.message }),
        onResult: r => {
          const shot = scopeShots.find(s => s.id === r.shotId);
          const itemId = String(r.shotId);
          const item: ImageReviewItem = {
            id: itemId, kind: 'image', shotId: r.shotId, success: r.success,
            enhancedDataUrl: r.enhancedDataUrl, originalDataUrl: shot?.thumbnailDataUrl,
            prompt: r.prompt, seed: r.seed, tokenCost: r.tokenCost,
            historyId: r.historyId, error: r.error, accepted: r.success,
          };
          items.push(item);
          itemsRef.current.push(item);
          if (r.success && r.enhancedDataUrl) {
            completedMediaRef.current.set(itemId, r.enhancedDataUrl);
            setCompletedMedia(new Map(completedMediaRef.current));
            trackEvent('generate_succeeded', {
              model_id: imageModelId,
              generation_type: 'image',
              duration_ms: Date.now() - enhanceStartTime,
              credits_spent: r.tokenCost ?? 0,
            });
          }
        },
      });

      if (abort.signal.aborted) return;
      // Deduplicate — keep only the latest result per shot (retries produce duplicates)
      const deduped = new Map<string, ImageReviewItem>();
      for (const item of items) deduped.set(item.id, item);
      setReviewItems([...deduped.values()]);
      setStep('review');
    } catch (err) {
      if (abort.signal.aborted) {
        diagLog('AiGen', `handleEnhance: aborted — applying ${itemsRef.current.filter(i => i.success).length} completed items`);
        // Apply already-completed enhancements on abort (handleCancel already does this,
        // but this catch also fires for the workspace-close abort path)
        const successItems = itemsRef.current.filter((i): i is ImageReviewItem => i.kind === 'image' && i.success && !!i.enhancedDataUrl);
        if (successItems.length > 0) {
          if (deferredCreateRef.current) {
            // DEFERRED RUN — drop and refund, identical to handleCancel's arm and for its reasons
            // (there is no target shot; the flow's shot is an ANCHOR). This catch is the other half
            // of the same gesture: handleCancel calls abort() and this is where the rejection it
            // causes lands, so the two must agree or a cancel would refund here and paint there
            // depending only on which of them saw the item first.
            itemsRef.current = [];
            createdShotIdRef.current = null;
            refundAndCleanup(successItems);
          } else {
            applyBatchGenerations(successItems.map(item => ({
              shotId: item.shotId,
              enhancedDataUrl: item.enhancedDataUrl!,
              historyId: item.historyId!,
            })));
          }
        }
        return;
      }
      diagLog('AiGen', `handleEnhance: FAILED — ${err instanceof Error ? err.message : String(err)}`);
      console.error('Batch enhancement failed:', err);
      setGenerationOrigin(null);
      setStep('options');
      useUiStore.getState().showAiError(i18next.t('errors:enhancementStoppedTitle'), describeAiError(err));
    } finally {
      useAiStore.getState().setGeneratingImage(null);
    }
  }, [scopeShots, allScenes, separator, editablePrompt, metadataSuffix, imageModelId, startGeneration, project, textOnly, seedAspect, refundAndCleanup]);

  // ── Generate: Video / Continuous ──────────────────────────────────────

  const handleVideoGenerate = useCallback(async () => {
    if (scopeUnits.length === 0) return;

    const ps = useProjectStore.getState();
    const existingCount = scopeUnits.filter(u => {
      const shotId = u.type === 'continuous' ? u.fromShot.id : u.shot.id;
      return ps.videoShots.find(v => v.shotId === shotId);
    }).length;
    if (existingCount > 0) {
      const msg = existingCount === 1
        ? i18next.t('errors:replaceVideoSingleConfirm')
        : i18next.t('errors:replaceVideoMultipleConfirm', { count: existingCount });
      if (!window.confirm(msg)) return;
    }

    const videoScopeMap: Record<AiWorkspaceScope, 'single' | 'scenes' | 'all'> = {
      all: 'all',
      selectedScenes: 'scenes',
      selectedShots: 'single',
    };
    // task=continuous is always a single pair regardless of scope
    const videoScope = task === 'continuous' ? 'single' : videoScopeMap[scope];
    trackEvent('generate_tapped', {
      model_id: videoModelId,
      generation_type: task === 'continuous' ? 'video_continuous' : 'video',
      scope: videoScope,
      batch_size: scopeUnits.length,
    });
    const videoStartTime = Date.now();

    const abort = startGeneration();
    setGenerationOrigin('enhance');
    priorityQueueRef.current = [];
    // The one binding, not a fresh useAiStore.getState() read: a host that pins the video model
    // (forcedVideoModelId) has to reach the REQUEST as well as the badge, or the dialog quotes one
    // model and spends on another.
    const currentModelId = videoModelId;
    const firstUnit = scopeUnits[0];
    useAiStore.getState().setGeneratingVideo(firstUnit.type === 'shot' ? firstUnit.shot.id : firstUnit.fromShot.id);

    try {
      const sceneMap = new Map(
        allScenes.map(s => [s.id, { name: s.name, locationType: s.locationType, timeOfDay: s.timeOfDay }])
      );
      const items: VideoReviewItem[] = [];
      const processedKeys = new Set<string>();

      /** Generate a single video unit and record the result. */
      const processUnit = async (unit: GenerationUnit) => {
        const key = unitKey(unit);
        const sep = project?.documentSettings?.separator ?? '';
        // The board's Create dialog renders THIS message verbatim on its video arm
        // (BoardEnhanceDialog.tsx — only the image arm formats its own line), so a hardcoded
        // literal here put an English status under a translated header. The video wording is
        // "Generate", not "Enhance", on both surfaces, so one key serves them both.
        setProgress({
          current: items.length,
          total: scopeUnits.length,
          message: i18next.t('aiWorkspace:batchProgressGeneratingVideo', {
            shot: shotFullLabel(unit.type === 'continuous' ? unit.fromShot : unit.shot, allScenes, sep),
          }),
        });

        if (unit.type === 'continuous') {
          try {
            const { generateTransitionVideo } = await import('@/ai/transitionVideoOrchestrator.ts');
            const scene = sceneMap.get(unit.fromShot.sceneId) ?? { name: '', locationType: '', timeOfDay: '' };
            const result = await generateTransitionVideo(
              null, unit.fromShot, unit.toShot, scene,
              abort.signal, undefined,
              { modelId: currentModelId, customPrompt: editablePrompt, shotId: unit.fromShot.id },
            );
            const item: VideoReviewItem = {
              id: key, kind: 'video', unitType: 'shot', shotId: unit.fromShot.id,
              success: true, videoUrl: result.videoUrl,
              sourceThumb: unit.fromShot.thumbnailDataUrl,
              prompt: result.prompt, seed: result.seed,
              tokenCost: result.tokenCost, historyId: result.historyId, accepted: true,
            };
            items.push(item);
            itemsRef.current.push(item);
            completedMediaRef.current.set(key, result.videoUrl);
            setCompletedMedia(new Map(completedMediaRef.current));
            trackEvent('generate_succeeded', {
              model_id: currentModelId,
              generation_type: 'video_continuous',
              duration_ms: Date.now() - videoStartTime,
              credits_spent: result.tokenCost,
            });
          } catch (err) {
            if (abort.signal.aborted) return;
            items.push({
              id: key, kind: 'video', unitType: 'shot', shotId: unit.fromShot.id,
              success: false,
              sourceThumb: unit.fromShot.thumbnailDataUrl,
              error: err instanceof Error ? err.message : 'Unknown error', accepted: false,
            });
          }
        } else {
          try {
            const { generateSingleVideo } = await import('@/ai/videoOrchestrator.ts');
            const scene = sceneMap.get(unit.shot.sceneId) ?? { name: '', locationType: '', timeOfDay: '' };
            const audioHint = aiAudioHints[unit.shot.sceneId] ?? undefined;
            // textOnly + seedAspect are the Create tab's pair, passed the same way it passes them:
            // in the REQUEST options, never by flipping the shared `textOnly` state (that flag is
            // the "Include image" checkbox's, carries stripPresetBoilerplate prompt-custody side
            // effects, and is force-cleared for every non-enhance task anyway). False here is the
            // shipped Timeline behaviour — animate the shot's own frame.
            //
            // ACCEPTED RESIDUAL (20260825): on a textOnly run the video history entry records the
            // GRAY PLATE as its input (persistVideoHistoryWithId stores whatever was uploaded),
            // where the image path deliberately records null so a later re-enhance has nothing
            // stale to re-run from. Pre-existing — the Create tab shipped it first — and not this
            // change's to reverse.
            const result = await generateSingleVideo(
              unit.shot, scene, abort.signal, undefined,
              {
                useMetadata: false, modelId: currentModelId, customVideoPrompt: editablePrompt,
                generateAudio, audioHint,
                textOnly: videoTextOnly, seedAspect,
              },
            );
            const item: VideoReviewItem = {
              id: key, kind: 'video', unitType: 'shot', shotId: unit.shot.id,
              success: true, videoUrl: result.videoUrl,
              // ACCEPTED RESIDUAL (20260825): on a textOnly run the input was a gray plate, so the
              // review's "Source" caption sits under the shot's real frame instead. Same in the
              // Create tab, which shipped it first.
              sourceThumb: unit.shot.thumbnailDataUrl,
              prompt: result.prompt, seed: result.seed,
              tokenCost: result.tokenCost, historyId: result.historyId, accepted: true,
            };
            items.push(item);
            itemsRef.current.push(item);
            completedMediaRef.current.set(key, result.videoUrl);
            setCompletedMedia(new Map(completedMediaRef.current));
            trackEvent('generate_succeeded', {
              model_id: currentModelId,
              generation_type: 'video',
              duration_ms: Date.now() - videoStartTime,
              credits_spent: result.tokenCost,
            });
          } catch (err) {
            if (abort.signal.aborted) return;
            items.push({
              id: key, kind: 'video', unitType: 'shot', shotId: unit.shot.id,
              success: false,
              sourceThumb: unit.shot.thumbnailDataUrl,
              error: err instanceof Error ? err.message : 'Unknown error', accepted: false,
            });
          }
        }
      };

      for (let idx = 0; idx < scopeUnits.length; idx++) {
        // Drain priority queue — retry/prioritize inserts shots here
        while (priorityQueueRef.current.length > 0) {
          if (abort.signal.aborted) break;
          const prioShot = priorityQueueRef.current.shift()!;
          const prioUnit = scopeUnits.find(u => u.type === 'shot' && u.shot.id === prioShot.id);
          if (!prioUnit) continue;
          const pk = unitKey(prioUnit);
          processedKeys.add(pk);
          await processUnit(prioUnit);
        }

        if (abort.signal.aborted) break;
        const unit = scopeUnits[idx];
        const key = unitKey(unit);
        if (processedKeys.has(key)) continue; // Already processed via priority queue
        processedKeys.add(key);
        await processUnit(unit);
      }

      if (abort.signal.aborted) return;
      setProgress({ current: scopeUnits.length, total: scopeUnits.length, message: i18next.t('aiWorkspace:batchProgressDone') });
      // Deduplicate — keep only the latest result per unit (retries produce duplicates)
      const deduped = new Map<string, VideoReviewItem>();
      for (const item of items) deduped.set(item.id, item);
      setReviewItems([...deduped.values()]);
      setStep('review');
    } catch (err) {
      if (abort.signal.aborted) {
        const successItems = itemsRef.current.filter(i => i.success);
        if (successItems.length > 0) refundAndCleanup(successItems);
        return;
      }
      console.error('Batch video generation failed:', err);
      setGenerationOrigin(null);
      setStep('options');
      useUiStore.getState().showAiError(i18next.t('errors:videoGenerationStoppedTitle'), describeAiError(err));
    } finally {
      useAiStore.getState().setGeneratingVideo(null);
    }
  }, [scopeUnits, allScenes, editablePrompt, metadataSuffix, generateAudio, aiAudioHints,
    startGeneration, videoModelId, videoTextOnly, seedAspect]);

  // ── Unified generate handler ──────────────────────────────────────────

  const handleGenerate = useCallback(() => {
    // 20260921 — EXAMPLE-ONLY (design §2.1). This is the FREE baked generation; routing a
    // characters run through it would hand it a pre-rendered sketch instead of the real, paid
    // generation its own beats are made of.
    const tut = useTutorialStore.getState();
    if (selectExampleTourActive(tut) && tut.step === 'render' && isEnhance && scopeShots.length === 1) {
      void runBakedGeneration(scopeShots[0]);
      return;
    }
    if (isEnhance) handleEnhance();
    else handleVideoGenerate();
  }, [isEnhance, handleEnhance, handleVideoGenerate, runBakedGeneration, scopeShots]);

  // ── Review handlers ───────────────────────────────────────────────────

  /**
   * Mirror the review's accept flags onto itemsRef.
   *
   * itemsRef is the ONLY list the unmount teardown can see (reviewItems is state, and a close can
   * land before the last setState commits), so it has to carry the user's decisions rather than the
   * accepted-on-creation defaults, or closing after rejecting a frame would write it anyway.
   */
  const syncAcceptance = useCallback((next: readonly ReviewItem[]) => {
    const acceptedById = new Map(next.map(i => [i.id, i.accepted]));
    itemsRef.current = itemsRef.current.map(item =>
      acceptedById.has(item.id) ? { ...item, accepted: acceptedById.get(item.id)! } : item
    );
  }, []);

  const handleAcceptAll = useCallback(() => {
    const next = reviewItems.map(item => ({ ...item, accepted: item.success }));
    syncAcceptance(next);
    setReviewItems(next);
  }, [reviewItems, syncAcceptance]);

  const handleRejectAll = useCallback(() => {
    const next = reviewItems.map(item => ({ ...item, accepted: false }));
    syncAcceptance(next);
    setReviewItems(next);
  }, [reviewItems, syncAcceptance]);

  const handleToggleItem = useCallback((id: string) => {
    const next = reviewItems.map(item =>
      item.id === id ? { ...item, accepted: !item.accepted } : item
    );
    syncAcceptance(next);
    setReviewItems(next);
  }, [reviewItems, syncAcceptance]);

  const acceptedCount = useMemo(
    () => reviewItems.filter(i => i.accepted && i.success).length,
    [reviewItems]
  );

  // ── Discard all ─────────────────────────────────────────────────────

  const handleDiscard = useCallback(() => {
    const successItems = reviewItems.filter(i => i.success);
    refundAndCleanup(successItems);
    // Ownership, taken SYNCHRONOUSLY. The board dialog discards and closes itself inside one batch,
    // so the unmount teardown can run before any of the setStates below commit — and re-applying a
    // generation that was just refunded is the worst outcome this hook has. The ref clear is what
    // makes that impossible; nothing here may rely on `step` having moved.
    itemsRef.current = [];
    createdShotIdRef.current = null;
    // The selection is KEPT (2026-08-25, was clearSelection). Coming back from a review the user
    // is still working on the same shot — the options panel should still be pointed at it, and the
    // close-path sync to the Timeline needs something to point at. Emptying it also flipped
    // StyleGradeTab's scope back to All Shots behind their back.
    setGenerationOrigin(null);
    setStep('options');
  }, [reviewItems, refundAndCleanup]);

  // ── Apply ─────────────────────────────────────────────────────────────

  /**
   * RESOLVES `true` ONLY WHEN THE REVIEW IS RESOLVED (20260828) — i.e. when finish() ran and there
   * is nothing left on this surface to keep. `false` means the opposite and is not cosmetic: the
   * review is STILL LIVE, itemsRef still holds the paid generation, and the user is meant to press
   * Apply again or Discard.
   *
   * A host that closes itself on Apply MUST branch on this. The board dialog used to close in a
   * `.finally`, which on a failed deferred create tore the dialog down with the review still armed
   * — and the unmount teardown then ran resolvePendingReviewOnTeardown, which created a SECOND
   * shot from the same Apply the user had just been told had failed. Void was the bug.
   *
   * Deliberately a boolean and not the shot id: two of the three `true` paths never touch a shot
   * (an empty accepted set, and the plain in-place apply), so an id would be null on paths that
   * succeeded — a discriminator that lies. Hosts that need the created id capture it themselves
   * (BoardEnhanceDialog.deferredCreatedShotIdRef).
   */
  const handleApply = useCallback(async (): Promise<boolean> => {
    if (applyBusy) return false;
    lastAppliedShotIdRef.current = null;

    /**
     * Every terminal path out of Apply. Clearing itemsRef is not housekeeping: it is how the unmount
     * teardown knows this review has been resolved, so an apply followed by a close (which is what
     * the board dialog and the tutorial's finale both do, inside one batch) stays a single write.
     */
    const finish = (selectShotId?: number) => {
      itemsRef.current = [];
      createdShotIdRef.current = null;
      // The selection is KEPT (2026-08-25, was clearSelection) — same reasoning as handleDiscard.
      // The one case that MOVES it is "Add as new shot": the shot the user is now looking at is the
      // one this apply just created, not the anchor it was generated from.
      if (selectShotId != null) setSelectedShotIds(new Set([selectShotId]));
      // …and the per-tab overrides go with it, on EVERY apply. Once an apply has landed, the shared
      // selection IS the context — a `createTargetShotId` left pointing at the anchor outranks it in
      // effectiveCreateShot, so the options panel would re-open on the anchor and the close-path
      // Timeline sync (which mirrors the ACTIVE tab's effective shot) would hand back the anchor too,
      // silently breaking the "the selection moves to the new shot" promise. Nulling both is the same
      // collapse unifySelectionForTabSwitch does at a tab boundary, applied at an apply boundary.
      setCreateTargetShotId(null);
      setTouchUpTargetShotId(null);
      setGenerationOrigin(null);
      setStep('options');
    };

    const accepted = reviewItems.filter(i => i.accepted && i.success);
    if (accepted.length === 0) { finish(); return true; }

    // Refund rejected successful items
    const rejected = reviewItems.filter(i => !i.accepted && i.success);
    if (rejected.length > 0) {
      refundAndCleanup(rejected);
    }

    /**
     * "Add as new shot" — Unity owns shot creation (web UI = dumb shell), so this is a create-then-
     * apply round trip rather than a write. readOnly is checked HERE, on the whole chain, not just
     * on the apply: applyBatchGenerations/attachVideo already no-op under readOnly, but insertShot
     * has no such guard and would otherwise leave a shot behind with nothing on it.
     */
    /**
     * A DEFERRED run (the board's Add-shot chooser, 20260828) takes this same chain with ONE
     * substitution: the shot is created by the host's own creator instead of by insertShotForCreate,
     * because the board owns `addShotToScene` (see AiWorkspaceFlowHost.onCreateShotForApply). It
     * qualifies whatever its generationOrigin is — the board dialog's Create arm reaches here as
     * 'enhance', not 'create', so testing the origin would silently skip it and paint the anchor.
     * Everything else below — the capture-first block, createdShotIdRef's retry, applyMediaToShot,
     * the applyBusy try/finally, finish(appliedShotId) — is already right for it.
     */
    const deferredCreate = hostRef.current?.onCreateShotForApply ?? null;
    const addAsNewShot = (deferredCreate != null
      || (generationOrigin === 'create' && createAddAsNewShot))
      && capabilities.bridge
      && !isReadOnly();

    if (addAsNewShot) {
      // CAPTURE FIRST. The insert's repush is a full project reload — it resets
      // aiStore.generationHistory, projectStore.videoShots and the undo stack — so every value the
      // apply needs has to be a plain local before a single byte goes to Unity.
      const imageItem = accepted.find((i): i is ImageReviewItem => i.kind === 'image' && !!i.enhancedDataUrl);
      const videoItem = accepted.find((i): i is VideoReviewItem => i.kind === 'video' && !!i.videoUrl);
      const anchorShotId = imageItem?.shotId ?? videoItem?.shotId;
      const videoUrl = imageItem ? undefined : videoItem?.videoUrl;
      if (anchorShotId == null) { finish(); return true; }

      const errorTitle = i18next.t(
        imageItem ? 'errors:enhancementStoppedTitle' : 'errors:videoGenerationStoppedTitle',
      );

      /** The shot the media actually landed on — handed to finish() so the selection follows it. */
      let appliedShotId: number | null = null;
      // The latch goes up BEFORE the (awaited) poster capture below — handleApply's re-entrancy
      // guard reads the STATE, so leaving it down across that await would let a second press start a
      // second chain, and a second shot.
      setApplyBusy(true);
      applyBusyRef.current = true;
      try {
        /**
         * The video arm's poster is captured HERE, before the insert, because the insert's repush is
         * a full loadProject and revokeOldBlobUrls (store/actions.ts) kills every generationHistory
         * objectUrl — this video's blob: among them. Extracting after the insert always failed, so
         * the new shot kept the ANCHOR's live 3D render with a play badge over it (20260909, owner).
         * The same repush is why the retime cannot measure the file either; `duration` is the length
         * the model was asked for, recorded on the review item at generate time.
         * A failed extraction is `null` — not fatal, and attachVideo's own fallback still runs.
         */
        const media: CreatedShotMedia = {
          enhancedDataUrl: imageItem?.enhancedDataUrl,
          videoUrl,
          historyId: imageItem?.historyId ?? videoItem?.historyId,
          posterBase64: videoUrl
            ? await import('@/utils/videoFrame.ts')
              .then(({ extractVideoFirstFrame }) => extractVideoFirstFrame(videoUrl))
              .catch(() => null)
            : null,
          durationSeconds: videoUrl ? videoItem?.duration ?? null : null,
          // Same reason, same moment (20260925, owner item 15c): the bytes, so the attach after the
          // insert can play a freshly minted URL instead of the blob the repush revokes.
          videoBytes: videoUrl ? await readAssetBytes(videoUrl).catch(() => null) : null,
        };
        /**
         * RETRY SEMANTICS. Everything past the insert can throw — the dynamic imports, the
         * applyBatchGenerations write, attachVideo/retimeShotToVideo — and by then the shot EXISTS.
         * The user is left in review holding a real, paid generation and an empty new shot, so
         * Apply has to stay pressable; what it must never do is create a second shot. The id is
         * therefore recorded the instant the insert lands, and a retry skips straight to the media
         * write against it. (Discard, and every completed Apply, clear it again.)
         */
        let targetShotId = createdShotIdRef.current;
        if (targetShotId == null) {
          // The deferred creator reports its OWN failures (the board toasts the ack's messageKey,
          // exactly as runAddShot does), so it takes no onError — a second dialog on top of that
          // toast would say the same thing twice. Both contracts agree on the return: null means
          // "no shot was created", and the branch below is the same either way.
          targetShotId = deferredCreate
            ? await deferredCreate()
            : await insertShotForCreate(anchorShotId, (msg) => {
              useUiStore.getState().showAiError(errorTitle, msg);
            });
          // Create failed: STAY in review with the generation intact and no refund — nothing was
          // lost but the insert, and the user can press Apply again or Discard. `false` is what
          // tells the host to stay open for that; see the contract note above handleApply.
          if (targetShotId == null) return false;
          createdShotIdRef.current = targetShotId;
        }
        await applyMediaToShot(targetShotId, media);
        appliedShotId = targetShotId;
      } catch (err) {
        // Same deal as a failed create, one step later: the error is shown, the review survives
        // with the generation on it, and nothing is refunded because nothing was consumed.
        useUiStore.getState().showAiError(errorTitle, describeAiError(err));
        return false;
      } finally {
        // The latch MUST clear on every path, including a throw — leaving it set freezes Discard
        // and Apply both, and the only way out of the review would be losing the generation.
        applyBusyRef.current = false;
        setApplyBusy(false);
      }

      lastAppliedShotIdRef.current = appliedShotId;
      finish(appliedShotId ?? undefined);
      return true;
    }

    if (isEnhance) {
      const imageItems = accepted.filter((i): i is ImageReviewItem => i.kind === 'image' && !!i.enhancedDataUrl);
      applyBatchGenerations(imageItems.map(item => ({
        shotId: item.shotId,
        enhancedDataUrl: item.enhancedDataUrl!,
        historyId: item.historyId!,
      })));
      // Import's own confirmation, and ONLY after a write that actually happened. A generated
      // result announces itself — the review's before/after is still on screen a frame ago and the
      // grid tile visibly changes — but a dropped file otherwise disappears into the panel with no
      // acknowledgement at all. (The old ImportTab fired this unconditionally, including under
      // readOnly where applyBatchGenerations no-ops; gating on a non-empty batch is the closest
      // this arm can get without changing applyBatchGenerations' void signature.)
      if (generationOrigin === 'import' && imageItems.length > 0) {
        showToast(i18next.t('aiWorkspace:importApplied'), 'success');
      }
    } else {
      const videoItems = accepted.filter((i): i is VideoReviewItem => i.kind === 'video' && !!i.videoUrl);
      const ps = useProjectStore.getState();
      const { retimeToMatchVideo } = useAiStore.getState();
      for (const item of videoItems) {
        ps.attachVideo(item.shotId, item.videoUrl!, 'video/mp4', item.historyId);
        if (retimeToMatchVideo) await retimeShotToVideo(item.shotId, item.videoUrl!);
      }
    }

    // Tutorial: applying the baked sketch is the final beat — advance to finale and close
    // whichever surface is hosting this flow. Since 2026-08-21 the tour's step 4 runs in the
    // board's Enhance dialog, so closeWorkspace resolves to the dialog's own close and the
    // user is already on the Board; reached the old way (AI Studio → the Timeline workspace)
    // it is still closeAiWorkspace, whose aiWorkspaceReturnToBoard one-shot routes back.
    // An IMPORT apply is excluded: the beat this advances is "you generated a frame", and dropping
    // a file in mid-tour is not it — the tour would jump to its finale and close the workspace.
    // 20260921 — EXAMPLE-ONLY (design §2.1): `finale` is the Getting started tour's last beat. A
    // characters run's generations are ordinary work and must not close the surface under the user.
    const tut = useTutorialStore.getState();
    if (selectExampleTourActive(tut) && tut.step === 'render' && isEnhance && generationOrigin !== 'import' && accepted.length > 0) {
      tut.setStep('finale');
      closeWorkspace();
      // fall through to finish() — harmless after close, and its itemsRef clear is what stops the
      // unmount teardown re-applying what this call just applied.
    }

    finish();
    return true;
  }, [reviewItems, isEnhance, refundAndCleanup, closeWorkspace,
      applyBusy, generationOrigin, createAddAsNewShot]);

  // ── Progress items (for GenerationProgress rendering) ─────────────────

  const progressItems = useMemo(() => {
    if (isEnhance) {
      return scopeShots.map(shot => {
        // If there's a touch-up override, use its image as the source thumbnail
        const override = touchUpOverridesRef.current.get(shot.id);
        return {
          id: String(shot.id),
          completed: completedMedia.has(String(shot.id)),
          shot,
          shotId: shot.id,
          sourceThumb: override?.imageDataUrl ?? shot.thumbnailDataUrl,
          label: shotFullLabel(shot, allScenes, separator),
        };
      });
    }
    return scopeUnits.map(unit => ({
      id: unitKey(unit),
      completed: completedMedia.has(unitKey(unit)),
      unit,
      shotId: unit.type === 'shot' ? unit.shot.id : undefined,
      sourceThumb: unit.type === 'shot' ? unit.shot.thumbnailDataUrl : unit.fromShot.thumbnailDataUrl,
      label: unitLabel(unit, allScenes, separator),
    }));
  }, [isEnhance, scopeShots, scopeUnits, completedMedia, allScenes, separator]);

  // ── Revert / Restore pill ─────────────────────────────────────────────

  const videoShots = useProjectStore(s => s.videoShots);

  const hasActiveEnhancement = !!(currentShot?.historyId);
  const hasActiveVideo = !!(currentShot && videoShots.find(v => v.shotId === currentShot.id));
  const hasStaticImage = !!(currentShot?.hasStaticImage);
  const canRevert = hasActiveEnhancement || hasActiveVideo || hasStaticImage;

  /** All restorable entries for the current shot (matching current task type). */
  const restoreEntries = useMemo(() => {
    if (!currentShot || canRevert) return [];
    const history = useAiStore.getState().getHistoryForShot(currentShot.id);
    const assetFilter = isEnhance ? 'image' : 'video';
    return history.filter(h => h.assetType === assetFilter && h.objectUrl);
  }, [currentShot, canRevert, isEnhance]);

  const canRestore = restoreEntries.length > 0;
  const restoreLabel = canRestore
    ? (restoreEntries[restoreEntries.length - 1].assetType === 'video' ? 'Saved Video' : 'Saved Image')
    : '';

  const handleRevert = useCallback(() => {
    if (!currentShot) return;
    revertShotToLive(currentShot.id);
  }, [currentShot]);

  const handleRestore = useCallback((historyId?: string) => {
    if (!currentShot || restoreEntries.length === 0) return;
    // If a specific historyId is provided, use it; otherwise use the most recent
    const id = historyId ?? restoreEntries[restoreEntries.length - 1].id;
    reapplyHistoryEntry(id, currentShot.id);
  }, [currentShot, restoreEntries]);

  return {
    // State
    open, task, initialPair, step, isEnhance,
    // Shot & scope
    currentShot, scopeShots, scopeUnits, scope, effectiveSceneId,
    sceneOptions, allShotCount, continuousPairCount, continuousAvailable,
    handleScopeChange, handleTaskChange,
    overwriteExisting, setOverwriteExisting, hasStaticShotsInProject,
    skipApproved, setSkipApproved,
    // Model
    activeModelId, catalog,
    setSelectedModel,
    // The Create tab drives its own Image/Video dropdown, so activeModelId (derived from the
    // SHARED uiStore task) is the wrong id for it — it picks by createTask instead. Same two
    // aiStore fields the Enhance tab writes: model choice is shared between the two tabs.
    aiSelectedImageModel, aiSelectedVideoModel,
    // Prompt
    // The OWNED setter, deliberately under the raw setter's name: every consumer of this field is a
    // textarea onChange, i.e. the user, and none of them should have to know about the hand-off.
    editablePrompt, setEditablePrompt: setEditablePromptOwned,
    promptOpen, togglePromptOpen: useCallback(() => setPromptOpen(p => !p), []),
    handlePromptReset,
    generatedPrompt,
    stylePreset, handleStylePresetChange,
    textOnly, setTextOnly, seedAspect,
    aiUseMetadata, toggleAiUseMetadata, metadataSuffix, editableMetadata, setEditableMetadata,
    // Video options
    generateAudio, setGenerateAudio,
    retimeToMatchVideo, toggleRetimeToMatchVideo,
    aiAudioHints, setAiAudioHint, effectiveSceneId_forAudio: effectiveSceneId,
    // Cost
    costEstimate, estTotalSeconds, durationLimits, hasClamping,
    // Generation
    elapsedSeconds, progress, completedMedia, progressItems,
    handleGenerate, handleCancel,
    // Preview + queue reordering
    previewShotId, setPreviewShotId, handleRetry, handlePrioritize, handleTouchUpFromPreview,
    // Review
    reviewItems, acceptedCount,
    handleAcceptAll, handleRejectAll, handleToggleItem, handleApply, handleDiscard,
    // Touch Up
    handleTouchUpGenerate, aiSelectedTouchUpModel,
    touchUpPrompt, setTouchUpPrompt,
    // Create
    createPrompt, setCreatePrompt,
    createTask, setCreateTask,
    createAddAsNewShot, setCreateAddAsNewShot,
    createTargetShotId, setCreateTargetShotId, effectiveCreateShot,
    createProgressLabel, handleCreateGenerate,
    generationOrigin, applyBusy,
    lastAppliedShotIdRef,
    // Import
    startImportReview,
    // V2 selection
    selectedShotIds, setSelectedShotIds, toggleShotSelection, toggleSceneSelection, clearSelection,
    unifySelectionForTabSwitch,
    autoScopeSwitch, setAutoScopeSwitch,
    // V2 touch up target
    touchUpTargetShotId, setTouchUpTargetShotId, effectiveTouchUpShot,
    // Revert / Restore
    canRevert, canRestore, restoreLabel, restoreEntries, handleRevert, handleRestore,
    // Close
    closeWorkspace,
  };
}
